import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, Bath, BedDouble, Bell, Building2, CalendarClock, CalendarDays, Camera, Check, ChevronLeft, ChevronRight, Copy, Share, Clock, Eye, FilePen, FileText, HeartHandshake, Home, Image as ImageIcon, Kanban, KeyRound, LineChart, Link2, Lock, Mail, MapPin, MapPinHouse, MessageSquareText, Monitor, Plus, Radio, ScrollText, Search, Server, Settings, Signature, Smartphone, Sun, TreePalm, Wallet, Wifi, X, Zap } from "lucide-react";
import { BrandLogo } from "@/components/flow/BrandLogos";
import { BrandMark } from "@/components/flow/Brand";
import { StageBadge } from "@/components/flow/Primitives";
import { EASE } from "@/lib/motion";
import { brandStyle } from "@/lib/brandColor";
import { brandify } from "@/lib/brandify";
import { WordReveal, TEXT_EASE, useFontsReady } from "@/components/flow/TextReveal";
import { t as t0 } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* Every string in this file goes through `t`, which fills {brand}, {domain} and {ownerDomain}
   from the brand currently being rendered (set by the stage root before its children render). */
const brandVars = { brand: "DigiHome", domain: "digihome.no", ownerDomain: "owners.digihome.no" };
const t = (key, vars) => t0(key, vars ? { ...brandVars, ...vars } : brandVars);

/* ---------------------------------------------------------------------------
   HeroStage v9 — one property, one surface at a time.
   The owner's phone (tenant-branded) OR your Listed workspace — never both.
   Every movement is a state change. Transform/opacity only. Rendered at a
   fixed design size and scaled to the container.

   Time: a single pausable scene clock (requestAnimationFrame) drives every
   beat, typewriter, progress bar and scene change — so pause really pauses,
   1.5× really is 1.5×, and nothing drifts when the tab or section is hidden.
--------------------------------------------------------------------------- */

const THUMB = "/img/interior-living.webp";
const LIVE_IMG = "/img/interior-openplan.webp";
const SITE_IMG = "/img/showcase-apartment.webp";

/* The demo tenant's brand. Every colour below reads a `--demo-brand*` token, which the stage root
   sets from the `brand` prop (see lib/brandColor.js) — so a visitor's own brand (name · logo ·
   colour · domain) can be shown with a smooth colour transition (`@property` in index.css). */
export const DEFAULT_BRAND = { name: "DigiHome", color: "#D298FF", domain: "digihome.no", ownerDomain: "owners.digihome.no", logoUrl: "/brand/digihome-mark.svg", heroImageUrl: SITE_IMG };
const DemoBrand = createContext(DEFAULT_BRAND);
export const useDemoBrand = () => useContext(DemoBrand);
const BRAND = "var(--demo-brand)";
const BRAND_FG = "var(--demo-brand-fg)";
const BRAND_TEXT = "var(--demo-brand-text)";
const BRAND_DEEP = "var(--demo-brand-deep)";
const BRAND_LIGHT = "var(--demo-brand-light)";
const BRAND_SOFT = "var(--demo-brand-soft)";
const ADDRESS = "Rua da Prata 42";
const CITY = "Lisbon";

/* Each scene lists its narration `beats`: ms offsets at which the next caption
   appears — matched to the moment the same thing happens on screen. */
export const SCENES = [
  { id: "site", first: "phone", ms: 3400, beats: [0, 1200, 2500], day: 1, clock: "09:00" },
  { id: "capture", first: "phone", ms: 6000, beats: [0, 1000, 2700, 4800], day: 1, clock: "09:01" },
  { id: "details", first: "phone", ms: 7000, beats: [0, 3200, 4100, 6100], day: 1, clock: "09:01" },
  { id: "estimate", first: "phone", second: "desktop", switchAt: 3600, ms: 7400, board: true, beats: [0, 1500, 3600, 4600], day: 1, clock: "09:02" },
  { id: "qualify", first: "desktop", ms: 6800, tab: "overview", beats: [0, 900, 3400], day: 1, clock: "09:03" },
  { id: "proposal", first: "desktop", ms: 7800, tab: "offer", beats: [0, 2400, 6000, 6900], day: 1, clock: "09:12" },
  { id: "offer", first: "phone", second: "desktop", switchAt: 7700, ms: 10200, tab: "offer", beats: [0, 1700, 4500, 7100, 7800], day: 1, clock: "09:16" },
  { id: "sign", first: "phone", second: "desktop", switchAt: 3900, ms: 7200, tab: "agreement", beats: [0, 600, 2600, 3900], day: 1, clock: "14:31" },
  { id: "photo", first: "phone", second: "desktop", switchAt: 4400, ms: 8400, tab: "onboarding", beats: [0, 1300, 3700, 4500], day: 1, clock: "14:36" },
  { id: "onboard", first: "phone", second: "desktop", switchAt: 8400, ms: 11000, tab: "onboarding", beats: [0, 1200, 3100, 4500, 6800, 8400], day: 2, clock: "17:02" },
  { id: "live", first: "desktop", ms: 6600, tab: "overview", beats: [0, 1500, 2600, 3400], day: 2, clock: "18:40" },
];
const IDS = SCENES.map((s) => s.id);
/* Hero loop — the owner's phone only, no chapters or narration: website → address → details → estimate → offer → signature → photography → onboarding → live. */
export const LOOP_SCENES = [
  { id: "site", first: "phone", ms: 3400, beats: [] },
  { id: "capture", first: "phone", ms: 6000, beats: [] },
  { id: "details", first: "phone", ms: 7000, beats: [] },
  { id: "estimate", first: "phone", ms: 3800, beats: [] },
  { id: "offer", first: "phone", ms: 7600, beats: [] },
  { id: "sign", first: "phone", ms: 4600, beats: [] },
  { id: "photo", first: "phone", ms: 4800, beats: [] },
  { id: "onboard", first: "phone", ms: 9200, beats: [] },
  { id: "live", first: "phone", ms: 4200, beats: [] },
];
/* Five chapters — each spans one or more scenes; the chapter's progress line fills across all of them. */
const GROUP_DEFS = [
  { id: "enquiry", icon: MapPinHouse, scenes: ["site", "capture", "details", "estimate"] },
  { id: "offer", icon: FilePen, scenes: ["qualify", "proposal", "offer"] },
  { id: "agreement", icon: Signature, scenes: ["sign"] },
  { id: "onboarding", icon: KeyRound, scenes: ["photo", "onboard"] },
  { id: "live", icon: Radio, scenes: ["live"] },
];
/** Chapters for a given scene list: running time, per-scene span of the progress line, first index. */
function buildGroups(scenes) {
  const ids = scenes.map((s) => s.id);
  return GROUP_DEFS.map((g) => {
    const own = g.scenes.map((id) => scenes.find((s) => s.id === id)).filter(Boolean);
    const total = own.reduce((a, s) => a + s.ms, 0);
    let acc = 0;
    const span = {};
    own.forEach((s) => {
      span[s.id] = [acc / total, (acc + s.ms) / total];
      acc += s.ms;
    });
    return { ...g, scenes: own.map((s) => s.id), total, span, firstIndex: ids.indexOf(own[0].id) };
  });
}
export const GROUPS = buildGroups(SCENES);
/* The full flow (the "Watch the full flow" film on the homepage): seven key moments, one per beat of
   the funnel — the hero loop already shows the website, and "How it works" carries the overview. */
const STORY_IDS = ["capture", "estimate", "proposal", "offer", "sign", "onboard", "live"];
export const STORY_SCENES = STORY_IDS.map((id) => SCENES.find((s) => s.id === id));
export const STORY_GROUPS = buildGroups(STORY_SCENES);
const GROUP_OF = {};
GROUPS.forEach((g, gi) => g.scenes.forEach((id) => { GROUP_OF[id] = gi; }));
const STAGE = { w: 1040, h: 660 };
const MID = { w: 760, h: 660 };
const COMPACT = { w: 360, h: 648 };
const PHONE = { w: 304, h: 632 };
const SCREEN = { site: "entry", capture: "entry", details: "details", estimate: "estimate", qualify: "estimate", proposal: "estimate", offer: "offer", sign: "sign", photo: "photo", onboard: "onboard", live: "live" };

/* ---------- motion tokens (this file only) ---------- */
const SWAP_IN = { duration: 0.85, ease: [0.32, 0.72, 0, 1] };
const SWAP_OUT = { duration: 0.42, ease: [0.4, 0, 1, 1] };
const screen = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE, delay: 0.1, staggerChildren: 0.06, delayChildren: 0.16 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.26, ease: [0.4, 0, 1, 1] } },
};
const item = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};
const SPRING_TAP = { type: "spring", stiffness: 420, damping: 26 };

/* ======================================================================
   Scene clock — one pausable, rate-aware clock per stage.
   Subscribers get scene time in ms on every animation frame. Stopping the
   clock freezes every beat, caption, typewriter and progress bar at once.
====================================================================== */
function createClock() {
  let now = 0;
  let last = 0;
  let raf = 0;
  let rate = 1;
  let running = false;
  const subs = new Set();
  const emit = () => subs.forEach((fn) => fn(now));
  const tick = (ts) => {
    if (!running) return;
    // Clamp the frame delta so a hidden tab or a long GC pause never fast-forwards the scene.
    if (last) now += Math.min(ts - last, 64) * rate;
    last = ts;
    emit();
    raf = requestAnimationFrame(tick);
  };
  return {
    get now() { return now; },
    get rate() { return rate; },
    start() {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      last = 0;
    },
    reset(to = 0) {
      now = to;
      last = 0;
      emit();
    },
    setRate(r) { rate = r; },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}
const ClockCtx = createContext(null);
/** Milliseconds already elapsed in the scene when this surface mounted. Late-mounted
    surfaces show what has already happened instead of replaying it. */
const ClockOffset = createContext(0);

/** `true` once the clock passes `at` (scene ms) — bound to a specific clock. Immediate when reduced. */
function useAtWith(clock, at, reduced, key = "") {
  const [on, setOn] = useState(() => !!reduced || !clock || clock.now >= at);
  useEffect(() => {
    if (reduced || !clock) {
      setOn(true);
      return undefined;
    }
    if (clock.now >= at) {
      setOn(true);
      return undefined;
    }
    setOn(false);
    let unsub = null;
    unsub = clock.subscribe((now) => {
      if (now >= at) {
        setOn(true);
        if (unsub) unsub();
      }
    });
    return () => unsub && unsub();
  }, [clock, at, reduced, key]);
  return on;
}

/** `true` after `ms` of surface time (immediately when reduced motion). Timings are relative to the
    moment this component mounted; `raw` ignores the surface offset. Restarts when `key` changes. */
function useAfter(ms, reduced, key = "", raw = false) {
  const clock = useContext(ClockCtx);
  const offset = useContext(ClockOffset);
  const mountAt = useRef(null);
  const keyRef = useRef(key);
  if (mountAt.current === null || keyRef.current !== key) {
    mountAt.current = clock ? clock.now : 0;
    keyRef.current = key;
  }
  const wait = raw ? ms : Math.max(0, ms - offset);
  return useAtWith(clock, mountAt.current + wait, reduced, key);
}

/** Scene time in ms, refreshed ~10×/s. Infinity when reduced motion. */
function useClock(reduced) {
  const clock = useContext(ClockCtx);
  const [now, setNow] = useState(() => (reduced || !clock ? Infinity : clock.now));
  useEffect(() => {
    if (reduced || !clock) return undefined;
    let last = -1000;
    setNow(clock.now);
    return clock.subscribe((now) => {
      if (Math.abs(now - last) >= 90) {
        last = now;
        setNow(now);
      }
    });
  }, [reduced, clock]);
  return now;
}

/** Typewriter driven by the scene clock. mode: "run" types · "done" shows all · "idle" shows nothing. */
function useTyped(text, mode = "run", speed = 52, startDelay = 450, key = 0) {
  const clock = useContext(ClockCtx);
  const [n, setN] = useState(mode === "done" ? text.length : 0);
  useEffect(() => {
    if (mode === "idle") {
      setN(0);
      return undefined;
    }
    if (mode !== "run" || !clock) {
      setN(text.length);
      return undefined;
    }
    const start = clock.now + startDelay;
    setN(0);
    return clock.subscribe((now) => {
      const k = Math.max(0, Math.min(text.length, Math.floor((now - start) / speed)));
      setN((p) => (p === k ? p : k));
    });
  }, [text, mode, speed, startDelay, key, clock]);
  return { typed: text.slice(0, n), done: n >= text.length, count: n };
}

/** A motion value 0→1 (or from→to) that follows the scene clock over `ms`. Freezes with the clock. */
function useSceneProgress(clock, ms, active, reduced, from = 0, to = 1) {
  const mv = useMotionValue(active ? from : 0);
  useEffect(() => {
    if (!active) {
      mv.set(0);
      return undefined;
    }
    if (reduced || !clock) {
      mv.set(to);
      return undefined;
    }
    const set = (now) => mv.set(from + (to - from) * Math.min(1, Math.max(0, now / ms)));
    set(clock.now);
    return clock.subscribe(set);
  }, [clock, ms, active, reduced, from, to, mv]);
  return mv;
}

/* ---------- helpers ---------- */
/** Column width, viewport height, and how far the stage may bleed to the right (to the viewport edge). */
function useStageMetrics(ref) {
  const [m, setM] = useState(() => ({ col: 0, bleed: 0, xl: typeof window !== "undefined" && window.innerWidth >= 1280, vh: typeof window !== "undefined" ? window.innerHeight : 0, vw: typeof window !== "undefined" ? window.innerWidth : 0 }));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      setM({ col: r.width, bleed: Math.max(r.width, vw - r.left - 24), xl: vw >= 1280, vh: window.innerHeight, vw });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref]);
  return m;
}

/** `true` when the viewport matches `query` (SSR-safe, updates live). */
function useMediaQuery(query) {
  const [on, setOn] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = () => setOn(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return on;
}

/** Pointer tilt for the phone: two spring-smoothed motion values, read from pointer position over `ref`. */
function useTilt(enabled, ref) {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 110, damping: 18, mass: 0.7 });
  const sry = useSpring(ry, { stiffness: 110, damping: 18, mass: 0.7 });
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) {
      rx.set(0);
      ry.set(0);
      return undefined;
    }
    const clamp = (v) => Math.max(-1.3, Math.min(1.3, v));
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const px = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2));
      const py = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2));
      ry.set(px * 7);
      rx.set(py * -4.5);
    };
    const leave = () => {
      rx.set(0);
      ry.set(0);
    };
    el.addEventListener("pointermove", move, { passive: true });
    el.addEventListener("pointerleave", leave);
    return () => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [enabled, ref, rx, ry]);
  return useMemo(() => ({ rotateX: srx, rotateY: sry }), [srx, sry]);
}

const Tick = ({ delay = 0, reduced, size = 18, className }) => (
  <motion.span initial={reduced ? false : { scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay, type: "spring", stiffness: 300, damping: 22 }} className={cn("flex shrink-0 items-center justify-center rounded-full bg-success", className)} style={{ height: size, width: size }}>
    <Check className="text-white" style={{ height: size * 0.55, width: size * 0.55 }} strokeWidth={3} />
  </motion.span>
);

const Appear = ({ when, children, className, y = 6, ...rest }) => (
  <AnimatePresence initial={false}>
    {when && (
      <motion.div initial={{ opacity: 0, y }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} transition={{ duration: 0.45, ease: EASE }} className={className} {...rest}>
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

/** A soft ring that blooms where a finger lands. Render inside a `relative` element. */
const TapRipple = ({ when, reduced, dark = false }) => (
  <AnimatePresence>
    {when && !reduced && (
      <motion.span key="tap" aria-hidden initial={{ opacity: 0.85, scale: 0.4 }} animate={{ opacity: 0, scale: 1.5 }} transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }} className={cn("pointer-events-none absolute left-1/2 top-1/2 -ml-5 -mt-5 h-10 w-10 rounded-full", dark ? "bg-ink/15 ring-2 ring-ink/10" : "bg-white/40 ring-2 ring-white/50")} />
    )}
  </AnimatePresence>
);

/* ======================================================================
   Root
   Opening: when the stage first enters view, the tabs' hairline draws in,
   the labels and the chapter title rise, the phone floats up — and only
   then does Emma start typing. On xl the workspace bleeds past the
   container's right edge (the hero section clips horizontally).
====================================================================== */
const INTRO = { tabs: 0.1, title: 0.35, stage: 0.55, start: 1.6 };
/** When the stage is already in view at page load, the opening waits for the headline above it. */
const LOAD_LEAD = 0.8;
export function HeroStage({ className, brand: brandProp, mode = "story" }) {
  const loop = mode === "loop";
  const brand = { ...DEFAULT_BRAND, ...(brandProp || {}) };
  // Fill {brand}/{domain}/{ownerDomain} for every string rendered below this root.
  brandVars.brand = brand.name;
  brandVars.domain = brand.domain;
  brandVars.ownerDomain = brand.ownerDomain;
  const SCN = loop ? LOOP_SCENES : SCENES;
  const reduced = useReducedMotion();
  const clockRef = useRef(null);
  if (!clockRef.current) clockRef.current = createClock();
  const clock = clockRef.current;
  const root = useRef(null);
  const outer = useRef(null);
  const { col, xl: rail, vh, vw } = useStageMetrics(outer);
  // Story on wide screens: a chapter rail beside the stage. Everywhere else: chapters · title · stage · caption.
  const compact = loop ? col > 0 : col > 0 && col < 640;
  const mid = !compact && col > 0 && col < 1000;
  const design = compact ? COMPACT : mid ? MID : STAGE;
  const avail = col;
  // The loop may grow a little past 1:1 so the phone reads well beside the headline —
  // but on short desktop viewports it shrinks so the whole phone (panel and transport included) fits the fold.
  const fit = loop && vw >= 1024 && vh ? Math.max(430, vh - 340) / design.h : Infinity;
  const scale = avail ? Math.min(loop ? 1.12 : 1, avail / design.w, fit) : 1;
  // Reveal when the chapter list + title come into view (the stage itself may still be below the fold on phones).
  const revealedRaw = useInView(root, { amount: 0.12, once: true });
  const fonts = useFontsReady();
  const visible = useInView(outer, { amount: 0.1 });
  const mountedAt = useRef(0);
  // Decide the opening's lead time in the same render the reveal starts, so every delay agrees.
  const [intro, setIntro] = useState({ on: false, lead: 0 });
  useEffect(() => {
    mountedAt.current = performance.now();
  }, []);
  useEffect(() => {
    if (revealedRaw && fonts && !intro.on) setIntro({ on: true, lead: performance.now() - mountedAt.current < 1500 ? LOAD_LEAD : 0 });
  }, [revealedRaw, fonts, intro.on]);
  const revealed = intro.on || !!reduced;
  const lead = intro.lead;
  const revealedAt = useRef(0);
  useEffect(() => {
    if (revealed && !revealedAt.current) revealedAt.current = performance.now();
  }, [revealed]);
  // Enough of the stage must be on screen before the demo starts (phones: the headline fills the first fold).
  const ready = useInView(outer, { amount: compact ? 0.4 : 0.15 });
  const [started, setStarted] = useState(!!reduced);
  const [i, setI] = useState(reduced ? (loop ? 0 : IDS.length - 1) : 0);
  const iRef = useRef(i);
  iRef.current = i;
  const [cycle, setCycle] = useState(0);
  const scene = SCN[i];
  const playing = !reduced && started && visible;
  const sceneKey = `${scene.id}-${cycle}`;
  const switched = useAtWith(clock, scene.switchAt || 0, reduced, sceneKey) && !!scene.second;
  const surface = switched ? scene.second : scene.first;
  const offset = switched ? scene.switchAt : 0;
  const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const tilt = useTilt(finePointer && !reduced && surface === "phone", root);

  useEffect(() => {
    // The demo starts once the stage is actually on screen. If the viewer scrolled to it well
    // after the opening reveal, don't make them wait through the opening beat again.
    if (!revealed || started || !ready) return undefined;
    const late = revealedAt.current && performance.now() - revealedAt.current > 1500;
    const id = setTimeout(() => setStarted(true), (late ? 0.5 : INTRO.start + lead) * 1000);
    return () => clearTimeout(id);
  }, [revealed, started, lead, ready]);

  // Run the clock only while playing; stop on unmount.
  useEffect(() => {
    if (playing) clock.start();
    else clock.stop();
  }, [playing, clock]);
  useEffect(() => () => clock.stop(), [clock]);

  const goTo = useCallback((idx, { wrap = false } = {}) => {
    const len = SCN.length;
    const next = ((idx % len) + len) % len;
    clock.reset(0);
    setStarted(true);
    setI(next);
    if (wrap ? next === 0 : true) setCycle((c) => c + 1);
  }, [SCN.length, clock]);

  // Advance when the scene's time is up.
  useEffect(() => {
    if (!playing) return undefined;
    const ms = scene.ms;
    return clock.subscribe((now) => {
      if (now >= ms) goTo(iRef.current + 1, { wrap: true });
    });
  }, [playing, scene.ms, sceneKey, clock, goTo]);

  const jump = useCallback((idx) => goTo(idx), [goTo]);

  // A new brand (the visitor's site just loaded) → tell the story again from the first step, in their colours.
  const brandSig = `${brand.name}|${brand.color}|${brand.logoUrl || ""}`;
  const brandSigRef = useRef(brandSig);
  useEffect(() => {
    if (brandSigRef.current === brandSig) return;
    brandSigRef.current = brandSig;
    if (started && !reduced) goTo(0);
  }, [brandSig, started, reduced, goTo]);

  // The phone sits centred on the visible column.
  const phoneLeft = compact ? (design.w - PHONE.w) / 2 : Math.max(0, (Math.min(col, design.w * scale) / scale - PHONE.w) / 2);

  const stageCard = (
    <div ref={outer} className="relative w-full min-w-0" data-testid="hero-stage-card">
      <motion.div initial={reduced ? false : { opacity: 0, y: 56, scale: 0.96 }} animate={revealed ? { opacity: 1, y: 0, scale: 1 } : undefined} transition={{ delay: (loop ? 0.2 : INTRO.stage) + lead, duration: 1.4, ease: TEXT_EASE }} className="relative will-change-transform mx-auto" style={{ width: Math.round(design.w * scale), height: Math.round(design.h * scale), maxWidth: "100%", transformOrigin: "50% 100%" }}>
        <div style={{ width: design.w, height: design.h, transform: `scale(${scale})`, transformOrigin: "top left" }} className="absolute left-0 top-0">
          <Canvas scene={scene} index={i} surface={surface} offset={offset} reduced={!!reduced} cycle={cycle} started={started} compact={compact} mid={mid} phoneLeft={phoneLeft} tilt={tilt} loop={loop} />
        </div>
      </motion.div>
    </div>
  );

  return (
    <DemoBrand.Provider value={brand}>
      <ClockCtx.Provider value={clock}>
        <div ref={root} className={cn("w-full demo-brand-scope", className)} style={brandStyle(brand.color)} data-testid={loop ? "hero-loop" : "hero-stage"} data-mode={mode} data-brand={brand.name} data-stage={scene.id} data-surface={surface} data-started={started ? "true" : "false"} data-revealed={revealed ? "true" : "false"} data-playing={playing ? "true" : "false"} data-layout={loop ? "loop" : rail ? "rail" : "stacked"}>
          {/* warm the image cache so later screens never pop */}
          <span aria-hidden className="hidden"><img src={THUMB} alt="" /><img src={LIVE_IMG} alt="" /><img src={SITE_IMG} alt="" />{ONB_PHOTOS.map((p) => <img key={p} src={p} alt="" />)}</span>
          {loop ? (
            <>
              <StagePanel loop surface={surface} revealed={revealed} lead={lead} reduced={!!reduced}>{stageCard}</StagePanel>
              <LoopTransport scenes={SCN} index={i} clock={clock} revealed={revealed} lead={lead} reduced={!!reduced} onJump={jump} />
            </>
          ) : (
            /* One stable tree for both story layouts (the stage card must never remount — its
               in-view observers are bound to the element). Wide: chapter rail beside a cinematic
               stage. Stacked: chapter strip · title · stage · one caption at a time, like subtitles. */
            <div className={cn(rail ? "grid grid-cols-[360px_minmax(0,1fr)] gap-x-10 2xl:gap-x-14 items-start" : "flex flex-col")}>
              <div className="min-w-0">
                {rail ? (
                  <StoryRail index={i} clock={clock} started={started} revealed={revealed} reduced={!!reduced} lead={lead} onJump={jump} scene={scene} sceneKey={sceneKey} />
                ) : (
                  <ChapterStrip index={i} clock={clock} started={started} revealed={revealed} reduced={!!reduced} lead={lead} onJump={jump} />
                )}
              </div>
              <div className={cn("min-w-0", rail && "hidden")}>{!rail && <StoryTitle scene={scene} cycle={cycle} reduced={!!reduced} revealed={revealed} started={started} lead={lead} />}</div>
              <div className={cn("min-w-0", !rail && "mt-6 sm:mt-8")}>
                <StagePanel surface={surface} revealed={revealed} lead={lead} reduced={!!reduced}>{stageCard}</StagePanel>
              </div>
              <div className={cn("min-w-0", rail ? "hidden" : "mt-6 sm:mt-7")}>
                {!rail && (
                  <AnimatePresence mode="wait">
                    <motion.div key={sceneKey} exit={{ opacity: 0, y: -8, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } }}>
                      <Beats scene={scene} sceneKey={sceneKey} started={started} reduced={!!reduced} stacked />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </div>
          )}
        </div>
      </ClockCtx.Provider>
    </DemoBrand.Provider>
  );
}

/* ======================================================================
   Stage panel — the product shot. The loop sits on a quiet, brand-tinted
   surface; the story plays on a cinematic dark stage (deep brand → ink)
   with a glass chip naming whose screen we are on. Both adopt the brand
   tokens, so a visitor's colour shows at a glance.
====================================================================== */
function StagePanel({ children, surface, revealed, lead = 0, reduced, loop = false }) {
  const SurfaceIcon = surface === "phone" ? Smartphone : Monitor;
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 24 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: (loop ? 0.1 : INTRO.stage - 0.1) + lead, duration: 1.1, ease: TEXT_EASE }} className={cn("relative overflow-hidden -mx-5 sm:mx-0 rounded-none sm:rounded-[36px] grain", loop ? "hero-stage-panel" : "story-stage grain-dark")} data-testid="hero-stage-panel" data-kind={loop ? "loop" : "story"} data-surface={surface}>
      <span aria-hidden className={cn("pointer-events-none absolute inset-0 dot-grid-masked", loop ? "dot-grid-ink opacity-60" : "dot-grid-light opacity-70")} />
      {(
        <div className={cn("relative z-10 flex items-center justify-between gap-3 pt-5", loop ? "px-5 sm:px-6" : "px-5 sm:px-7")}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={surface} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35, ease: EASE }} className={cn("inline-flex max-w-full items-center gap-2 h-8 rounded-full border backdrop-blur-md pl-2.5 pr-3.5 text-[12.5px] font-medium", loop ? "border-ink/[0.08] bg-white/70 text-ink/80" : "border-white/10 bg-white/[0.08] text-white/85")} data-testid="hero-surface" data-surface={surface}>
              <SurfaceIcon className={cn("h-3.5 w-3.5 shrink-0", loop ? "text-ink/45" : "text-white/60")} strokeWidth={1.9} />
              <span className="truncate">{t(`l7.h.surface.${surface}`)}</span>
            </motion.span>
          </AnimatePresence>
          <span className={cn("inline-flex items-center gap-1.5 text-[11.5px]", loop ? "text-ink/45" : "text-white/40")} aria-hidden><span className="h-1.5 w-1.5 rounded-full bg-success pulse-dot" />{t("l13.stage.live")}</span>
        </div>
      )}
      <div className={cn("relative z-10", loop ? "px-5 sm:px-6 pt-4 sm:pt-5 pb-6 sm:pb-7" : "px-4 sm:px-7 pt-4 sm:pt-5 pb-5 sm:pb-7")}>{children}</div>
    </motion.div>
  );
}

/* ======================================================================
   Transport — six quiet segments (one per step) that fill with the clock,
   and the current step's name. Tapping a segment jumps to that step.
====================================================================== */
function Segment({ scene, active, done, clock, reduced, onClick }) {
  const mv = useSceneProgress(clock, scene.ms, active, reduced);
  return (
    <li className="flex-1 min-w-0">
      <button type="button" role="tab" aria-selected={active} aria-label={t(`l13.loop.${scene.id}`)} onClick={onClick} className="group relative block w-full h-8 focus-visible:outline-none" data-testid={`hero-transport-seg-${scene.id}`} data-active={active ? "true" : "false"} data-done={done ? "true" : "false"}>
        <span className={cn("absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] rounded-full overflow-hidden transition-[transform,background-color] duration-200 ease-editorial group-hover:scale-y-[2] group-focus-visible:scale-y-[2]", done ? "bg-ink/40" : "bg-ink/[0.12] group-hover:bg-ink/25")}>
          {active && <motion.span className="absolute inset-0 origin-left rounded-full bg-ink" style={{ scaleX: mv }} />}
        </span>
      </button>
    </li>
  );
}

function LoopTransport({ scenes, index, clock, revealed, lead = 0, reduced, onJump }) {
  const scene = scenes[index];
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 10 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: 0.7 + lead, duration: 0.9, ease: TEXT_EASE }} className="mt-4 sm:mt-5 w-full" data-testid="hero-transport" data-step={scene.id}>
      <ol className="flex items-center gap-1.5" role="tablist" aria-label={t("l13.tr.steps")}>
        {scenes.map((s, k) => (
          <Segment key={s.id} scene={s} active={k === index} done={k < index} clock={clock} reduced={reduced} onClick={() => onJump(k)} />
        ))}
      </ol>
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="relative min-w-0 flex-1 h-5 overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={scene.id} initial={{ opacity: 0, y: 9 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -9 }} transition={{ duration: 0.32, ease: EASE }} className="absolute inset-0 truncate text-[12.5px] text-ink/55 leading-5" data-testid="hero-transport-label">
              {t(`l13.loop.${scene.id}`)}
            </motion.span>
          </AnimatePresence>
        </span>
      </div>
    </motion.div>
  );
}

/* ======================================================================
   Chapters — five chapters, each spanning one or more scenes; a chapter's
   progress line fills continuously across every scene in it, driven by
   the scene clock (so it pauses with everything else).

   StoryRail (wide): a vertical list beside the stage. The active chapter
   opens like a drawer and holds the scene title and the live captions —
   past chapters are ticked, future ones wait.
   ChapterStrip (stacked): the same chapters as a row of pills.
====================================================================== */
function ChapterBar({ scene, span, clock, reduced, className = "bg-ink" }) {
  const [from, to] = span;
  const mv = useSceneProgress(clock, scene.ms, true, reduced, from, to);
  return <motion.span className={cn("absolute inset-0 origin-left", className)} style={{ scaleX: mv }} />;
}

function ChapterIcon({ icon: Icon, active, done, size = "md", onDark = false }) {
  const dims = size === "md" ? "h-9 w-9 rounded-[11px]" : "h-7 w-7 rounded-full";
  const glyph = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <motion.span animate={active ? { scale: [1, 1.08, 1] } : { scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className={cn("relative flex shrink-0 items-center justify-center border transition-colors duration-500 ease-editorial", dims, onDark ? (active ? "bg-white/15 border-transparent text-white" : "bg-transparent border-transparent text-current") : active ? "bg-ink border-ink text-white shadow-[0_10px_22px_-12px_rgba(21,19,15,0.7)]" : done ? "bg-fill border-transparent text-ink/60" : "bg-surface border-hairline text-ink/40 group-hover:text-ink/70 group-hover:border-ink/20")} aria-hidden>
      <AnimatePresence mode="wait" initial={false}>
        {done ? (
          <motion.span key="done" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }} transition={{ duration: 0.25, ease: EASE }} className="flex"><Check className={glyph} strokeWidth={2.6} /></motion.span>
        ) : (
          <motion.span key="icon" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }} transition={{ duration: 0.25, ease: EASE }} className="flex"><Icon className={glyph} strokeWidth={1.9} /></motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );
}

const StoryRail = React.memo(function StoryRail({ index, clock, started, revealed, reduced, lead = 0, onJump, scene, sceneKey }) {
  const gi = GROUP_OF[scene.id];
  const title = t(`l7.h.title.${scene.id}`);
  const intro = !started;
  const titleLead = intro ? INTRO.title + lead : 0.1;
  const play = intro ? revealed : true;
  const exit = { opacity: 0, y: -8, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } };
  return (
    <div className="relative pt-1" data-testid="hero-chapters" data-visible={revealed ? "true" : "false"} data-group={GROUPS[gi].id} data-layout="rail">
      <ol className="relative" role="tablist" aria-orientation="vertical">
        {GROUPS.map((g, k) => {
          const active = k === gi;
          const done = k < gi;
          return (
            <motion.li key={g.id} initial={reduced ? false : { opacity: 0, y: 8 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: INTRO.tabs + lead + k * 0.08, duration: 0.8, ease: TEXT_EASE }} className={cn("relative border-t border-hairline", k === GROUPS.length - 1 && "border-b")} data-testid={`hero-chapter-${g.id}`} data-active={active ? "true" : "false"} data-done={done ? "true" : "false"}>
              {/* progress — runs along the chapter's top hairline */}
              <span className="absolute left-0 right-0 -top-px h-[2px] overflow-hidden" aria-hidden>
                {done && <span className="absolute inset-0 bg-ink/25" />}
                {active && started && <ChapterBar key={`${scene.id}-${g.id}`} scene={scene} span={g.span[scene.id]} clock={clock} reduced={!!reduced} />}
              </span>
              <button type="button" role="tab" aria-selected={active} aria-label={t(`l7.h.group.${g.id}`)} onClick={() => onJump(g.firstIndex)} className={cn("group relative flex w-full items-center gap-3.5 py-3.5 text-left transition-colors duration-500 ease-editorial focus-visible:outline-none focus-visible:text-ink", active ? "text-ink" : done ? "text-ink/55 hover:text-ink" : "text-ink/40 hover:text-ink/70")} data-testid={`hero-chapter-${g.id}-button`}>
                <ChapterIcon icon={g.icon} active={active} done={done} />
                <span className={cn("min-w-0 flex-1 text-[15px] leading-5", active && "font-medium")} data-testid={`hero-chapter-${g.id}-label`}>{t(`l7.h.group.${g.id}`)}</span>
                <span className="relative h-5 min-w-0 max-w-[45%] overflow-hidden text-right text-[12px] leading-5 text-ink/45" aria-live={active ? "polite" : undefined}>
                  <AnimatePresence mode="wait" initial={false}>
                    {active && started && (
                      <motion.span key={scene.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className="block truncate" data-testid="hero-chapter-step">
                        {t(`l7.h.step.${scene.id}`)}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
              </button>
              <AnimatePresence initial={false}>
                {active && (
                  <motion.div key="body" initial={intro ? false : { height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ height: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.35, ease: EASE } }} className="overflow-hidden" data-testid="hero-chapter-body">
                    <div className="pl-[50px] pb-7 min-w-0">
                      <div data-testid="hero-narrator">
                        <AnimatePresence mode="wait">
                          <motion.div key={sceneKey} exit={exit}>
                            <WordReveal as="h3" text={title} delay={titleLead} stagger={0.06} duration={1.0} distance={18} reduced={!!reduced} play={play} className="font-display text-[30px] 2xl:text-[34px] leading-[1.05] text-ink text-balance min-h-[2.1em]" data-testid="hero-title-typed" />
                          </motion.div>
                        </AnimatePresence>
                      </div>
                      <div className="mt-4 min-w-0">
                        <AnimatePresence mode="wait">
                          <motion.div key={sceneKey} exit={exit}>
                            <Beats scene={scene} sceneKey={sceneKey} started={started} reduced={!!reduced} stacked={false} />
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
});

const ChapterStrip = React.memo(function ChapterStrip({ index, clock, started, revealed, reduced, lead = 0, onJump }) {
  const scene = SCENES[index];
  const gi = GROUP_OF[scene.id];
  const wide = useMediaQuery("(min-width: 640px)");
  return (
    <div className="relative -mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar" data-testid="hero-chapters" data-visible={revealed ? "true" : "false"} data-group={GROUPS[gi].id} data-layout="strip">
      <ol className="flex items-center gap-2" role="tablist">
        {GROUPS.map((g, k) => {
          const active = k === gi;
          const done = k < gi;
          return (
            <motion.li key={g.id} initial={reduced ? false : { opacity: 0, y: 6 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: INTRO.tabs + lead + k * 0.07, duration: 0.8, ease: TEXT_EASE }} className="shrink-0" data-testid={`hero-chapter-${g.id}`} data-active={active ? "true" : "false"} data-done={done ? "true" : "false"}>
              <button type="button" role="tab" aria-selected={active} aria-label={t(`l7.h.group.${g.id}`)} onClick={() => onJump(g.firstIndex)} className={cn("group relative inline-flex h-10 items-center gap-2 overflow-hidden rounded-full border pl-1.5 pr-3.5 text-left whitespace-nowrap transition-colors duration-500 ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30", active ? "bg-ink border-ink text-white" : done ? "bg-fill border-transparent text-ink/70 hover:text-ink" : "bg-surface border-hairline text-ink/50 hover:text-ink/80 hover:border-ink/20")} data-testid={`hero-chapter-${g.id}-button`}>
                <ChapterIcon icon={g.icon} active={active} done={done} size="sm" onDark={active} />
                {(active || wide) && (
                  <motion.span key={`label-${g.id}`} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }} className="text-[13px] font-medium" data-testid={`hero-chapter-${g.id}-label`}>
                    {t(`l7.h.group.${g.id}`)}
                  </motion.span>
                )}
                {active && started && (
                  <span className="absolute inset-x-0 bottom-0 h-[2px] overflow-hidden" aria-hidden>
                    <ChapterBar key={`${scene.id}-${g.id}`} scene={scene} span={g.span[scene.id]} clock={clock} reduced={!!reduced} className="bg-white/70" />
                  </span>
                )}
              </button>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
});

/* ======================================================================
   Narrator — whose screen we are on, the chapter title rises word by word,
   then the *beats*: short captions that appear at the exact moment the same
   thing happens on the surface. Desktop: a rail of beats (past ones dim).
   Stacked/mobile: one beat at a time, with progress dots.
====================================================================== */
/** How many beats have fired for this scene (scene clock). Waits for `active`. */
function useBeatCount(times, key, active, reduced) {
  const clock = useContext(ClockCtx);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (reduced) {
      setN(times.length);
      return undefined;
    }
    if (!active || !clock) {
      setN(0);
      return undefined;
    }
    const calc = (now) => times.filter((ms) => ms <= now).length;
    setN(calc(clock.now));
    return clock.subscribe((now) => {
      const k = calc(now);
      setN((p) => (p === k ? p : k));
    });
  }, [times, key, active, reduced, clock]);
  return n;
}

function Beats({ scene, sceneKey, started, reduced, stacked }) {
  const times = scene.beats;
  const n = useBeatCount(times, sceneKey, started, reduced);
  const text = (k) => brandify(t(`l8.h.beat.${scene.id}.${k}`));
  if (stacked) {
    return (
      <div className="sm:flex sm:items-start sm:gap-6" data-testid={`hero-caption-${scene.id}`} data-beat={n}>
        <div className="relative min-h-[76px] sm:min-h-[56px] sm:flex-1">
          <AnimatePresence mode="wait" initial={false}>
            {n > 0 && (
              <motion.p key={n} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }} transition={{ duration: 0.45, ease: EASE }} className="text-[15.5px] sm:text-[17px] text-ink/75 leading-[1.55] max-w-[60ch]" data-testid="hero-beat-active">
                {text(n)}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <div className="mt-3 sm:mt-2.5 flex items-center gap-1.5 shrink-0" aria-hidden>
          {times.map((ms, i) => (
            <span key={ms} className={cn("h-1 rounded-full transition-[width,background-color] duration-420 ease-editorial", i < n ? "w-5 bg-ink" : "w-1.5 bg-ink/20")} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-[236px]" data-testid={`hero-caption-${scene.id}`} data-beat={n} aria-live="polite">
      <ol className="relative">
        <span aria-hidden className="absolute left-[5px] top-3 bottom-3 w-px bg-hairline" />
        {times.slice(0, n).map((ms, i) => {
          const active = i === n - 1;
          return (
            <li key={ms} className={cn("relative flex items-start gap-4 py-2", !reduced && "beat-enter")} data-testid={`hero-beat-${i + 1}`} data-active={active ? "true" : "false"}>
              <span className={cn("relative mt-[7px] h-[11px] w-[11px] shrink-0 rounded-full ring-[3px] ring-canvas transition-colors duration-500", active ? "bg-ink" : "bg-ink/25")} />
              <p className={cn("text-[15.5px] leading-[1.55] transition-colors duration-500 ease-editorial", active ? "text-ink" : "text-ink/45")}>{text(i + 1)}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The chapter title for stacked layouts (below the chapter strip, above the stage). */
function StoryTitle({ scene, cycle, reduced, revealed, started, lead: loadLead = 0 }) {
  const title = t(`l7.h.title.${scene.id}`);
  const intro = !started;
  const lead = intro ? INTRO.title + loadLead : 0.1;
  const play = intro ? revealed : true;
  return (
    <div className="min-w-0" data-testid="hero-narrator">
      <AnimatePresence mode="wait">
        <motion.div key={`${scene.id}-${cycle}`} exit={{ opacity: 0, y: -8, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } }}>
          <WordReveal as="h3" text={title} delay={lead} stagger={0.06} duration={1.0} distance={18} reduced={!!reduced} play={play} className="mt-7 sm:mt-9 font-display text-[32px] sm:text-[40px] leading-[1.04] text-ink text-balance min-h-[2.08em]" data-testid="hero-title-typed" />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ======================================================================
   Canvas — one surface, full attention. Slow, overlapping crossfade.
   Behind the phone: a halo in the brand colour (breathes slowly), and the
   phone itself tilts a few degrees toward the pointer.
====================================================================== */
const Canvas = React.memo(function Canvas({ scene, index, surface, offset, reduced, float = !reduced, cycle, started, compact, mid, phoneLeft, tilt, loop }) {
  const H = compact ? COMPACT.h : STAGE.h;
  const top = (H - PHONE.h) / 2;
  return (
    <div className="relative h-full w-full" data-testid="hero-canvas" data-layout={surface}>
      <AnimatePresence mode="popLayout" initial={false}>
        {surface === "phone" ? (
          <motion.div key="phone" initial={reduced ? false : { opacity: 0, scale: 0.96, y: 18 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: -10, transition: SWAP_OUT }} transition={SWAP_IN} className="absolute" style={{ left: phoneLeft, top, width: PHONE.w, height: PHONE.h, transformOrigin: "50% 50%" }} data-testid="hero-phone-frame">
            {/* brand halo — light behind the phone; wider on the dark story stage */}
            <span aria-hidden className={cn("phone-halo pointer-events-none absolute rounded-full", loop ? "opacity-100" : "opacity-90")} style={{ left: compact ? -30 : loop ? -70 : -220, right: compact ? -30 : loop ? -70 : -220, top: loop ? 60 : -10, bottom: loop ? 30 : 10, background: "radial-gradient(closest-side, var(--demo-brand-glow) 0%, var(--demo-brand-soft) 48%, transparent 78%)", filter: loop || compact ? "blur(28px)" : "blur(40px)" }} data-testid="hero-phone-halo" />
            <motion.div className="relative h-full w-full will-change-transform" style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformPerspective: 1200 }}>
              <div className={cn("relative h-full w-full", float && "phone-float")}>
                <ClockOffset.Provider value={offset}>
                  <Phone scene={scene} reduced={reduced} cycle={cycle} started={started} />
                </ClockOffset.Provider>
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div key="desktop" initial={reduced ? false : { opacity: 0, scale: 0.975, y: 18 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.99, y: -10, transition: SWAP_OUT }} transition={SWAP_IN} className="absolute inset-0 will-change-transform" style={{ transformOrigin: "50% 50%" }} data-testid="hero-desktop-frame">
            <ClockOffset.Provider value={offset}>
              <Desktop scene={scene} index={index} reduced={reduced} cycle={cycle} compact={compact} mid={mid} />
            </ClockOffset.Provider>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ======================================================================
   Desktop — the Listed workspace.
   Icon rail · top bar with breadcrumb · ONE focused page: the pipeline
   board, or the property record (header, sub-tabs, content, activity rail).
====================================================================== */
const NAV = [
  ["pipeline", Kanban],
  ["properties", Building2],
  ["proposals", FileText],
  ["outbox", Mail],
  ["settings", Settings],
];
const NAV_ACTIVE = { estimate: "pipeline", qualify: "properties", proposal: "proposals", offer: "proposals", sign: "proposals", photo: "properties", onboard: "properties", live: "properties" };
const TABS = ["overview", "offer", "agreement", "onboarding", "activity"];

/* The record's activity — every chapter appends to it. `at` = ms into that scene. */
const TIMELINE = [
  ["enquiry", "capture", 0, "09:01"],
  ["details", "details", 0, "09:01"],
  ["estimate", "estimate", 0, "09:02"],
  ["summary", "qualify", 900, "09:03"],
  ["reviewed", "qualify", 3400, "09:05"],
  ["previewed", "proposal", 2500, "09:12"],
  ["offer_sent", "proposal", 6000, "09:14"],
  ["opened", "offer", 2300, "09:16"],
  ["added", "offer", 4500, "09:20"],
  ["accepted", "offer", 7100, "09:21"],
  ["signed", "sign", 2600, "14:32"],
  ["photo", "photo", 5000, "14:37"],
  ["wifi", "onboard", 2100, "Day 2"],
  ["onboarded", "onboard", 6700, "Day 2"],
  ["created", "live", 1500, "Day 2"],
  ["live", "live", 3400, "Day 2"],
];

function Desktop({ scene, index, reduced, cycle, compact = false, mid = false }) {
  const id = scene.id;
  const key = `${id}-${cycle}`;
  const now = useClock(reduced, key);
  const EST = IDS.indexOf("estimate");
  const landed = useAfter(index === EST ? 900 : 0, reduced, key, true);
  const arrived = index >= EST && landed;
  const sent = id === "proposal" && now >= 6000;
  const previewing = id === "proposal" && now >= 2500 && now < 5600;
  const accepted = id === "offer" && now >= 7100;
  const signDone = id === "sign" && now >= 2600;
  const onbDone = id === "onboard" && now >= 5200;

  let badge = ["qualified", t("l4.frame.status.property")];
  if (id === "proposal") badge = sent ? ["offer", t("l7.h.step.proposal")] : ["qualified", t("l4.frame.status.property")];
  if (id === "offer") badge = accepted ? ["signed", t("l5.d.status.accepted")] : ["offer", t("l7.h.step.proposal")];
  if (id === "sign") badge = signDone ? ["signed", t("l4.frame.status.signed")] : ["offer", t("l5.d.status.accepted")];
  if (id === "photo") badge = ["onboarding", t("stage.onboarding")];
  if (id === "onboard") badge = onbDone ? ["ready", t("l4.frame.status.ready")] : ["onboarding", t("stage.onboarding")];
  if (id === "live") badge = ["live", t("l4.frame.status.live")];

  const crumb = scene.board ? [t("l7.d.crumb.pipeline")] : [t("l7.d.crumb.properties"), ADDRESS];

  return (
    <div className="relative h-full w-full flex rounded-[22px] border border-ink/[0.09] bg-canvas text-ink overflow-hidden shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_60px_120px_-50px_rgba(21,19,15,0.45),0_24px_48px_-32px_rgba(21,19,15,0.3)] select-none" data-testid="hero-desktop" data-view={scene.board ? "board" : scene.tab} data-preview={previewing ? "open" : "closed"}>
      {/* Fullscreen preview of the owner page — exactly what Emma will see */}
      <AnimatePresence>{previewing && <OfferPreview key="preview" reduced={reduced} compact={compact} mid={mid} />}</AnimatePresence>
      {/* Icon rail */}
      <aside className={cn("shrink-0 flex flex-col items-center border-r border-hairline bg-surface/70", compact ? "w-11 py-2.5" : "w-[60px] py-3.5")} data-testid="hero-sidebar">
        <BrandMark className={cn("rounded-[7px]", compact ? "h-6 w-6" : "h-7 w-7")} />
        <ul className={cn("flex flex-col items-center gap-1", compact ? "mt-3" : "mt-5")}>
          {NAV.map(([k, Icon]) => {
            const active = NAV_ACTIVE[id] === k;
            return (
              <li key={k} className={cn("relative flex items-center justify-center rounded-[10px]", compact ? "h-8 w-8" : "h-9 w-9")} data-testid={`hero-nav-${k}`} data-active={active ? "true" : "false"}>
                {active && <motion.span layoutId="hero-rail-active" className="absolute inset-0 rounded-[10px] bg-ink" transition={{ duration: 0.45, ease: EASE }} />}
                <Icon className={cn("relative", compact ? "h-3.5 w-3.5" : "h-4 w-4", active ? "text-white" : "text-ink/55")} strokeWidth={1.8} />
              </li>
            );
          })}
        </ul>
        <span className={cn("mt-auto flex items-center justify-center rounded-full bg-ink text-white font-bold", compact ? "h-6 w-6 text-[9px]" : "h-8 w-8 text-[10.5px]")}>SJ</span>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <div className={cn("shrink-0 flex items-center justify-between border-b border-hairline bg-surface/70", compact ? "h-10 px-3" : "h-12 px-5")}>
          <div className="flex items-center gap-1.5 min-w-0 text-[12.5px]">
            <span className="text-ink/45 shrink-0">{t("l4.frame.workspace")}</span>
            {crumb.map((c, k) => (
              <React.Fragment key={c}>
                <ChevronRight className="h-3 w-3 text-ink/30 shrink-0" />
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span key={c} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -3 }} transition={{ duration: 0.3, ease: EASE }} className={cn("truncate", k === crumb.length - 1 ? "text-ink font-medium" : "text-ink/55")}>{c}</motion.span>
                </AnimatePresence>
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center gap-2.5">
            {!compact && <span className={cn("flex items-center gap-2 h-8 rounded-[9px] border border-hairline bg-canvas px-2.5 text-[11.5px] text-ink/40", mid ? "w-[150px]" : "w-[200px]")}><Search className="h-3.5 w-3.5" /> {t("l7.d.search")} <span className="ml-auto font-mono text-[10px] text-ink/35">⌘K</span></span>}
            <span className="relative flex h-8 w-8 items-center justify-center rounded-[9px] text-ink/60">
              <Bell className="h-3.5 w-3.5" />
              {index === EST && arrived && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={SPRING_TAP} className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-lavenderCta ring-2 ring-surface" />}
            </span>
          </div>
        </div>

        {/* Page */}
        <main className={cn("flex-1 min-h-0 flex flex-col", compact ? "p-3" : "p-6")}>
          <AnimatePresence mode="popLayout" initial={false}>
            {scene.board ? (
              <motion.div key="board" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8, transition: { duration: 0.25 } }} transition={{ duration: 0.55, ease: EASE }} className="flex-1 min-h-0 flex flex-col" data-testid="hero-panel-pipeline">
                <PipelineView arrived={arrived} reduced={reduced} compact={compact} />
              </motion.div>
            ) : (
              <motion.div key="record" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8, transition: { duration: 0.25 } }} transition={{ duration: 0.55, ease: EASE }} className="flex-1 min-h-0 flex flex-col" data-testid="hero-panel-record">
                <Record scene={scene} index={index} badge={badge} now={now} reduced={reduced} cycle={cycle} compact={compact} mid={mid} />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

/* ---------- the property record: header · sub-tabs · content + activity ---------- */
const ACTION = { qualify: "l7.d.action.offer", proposal: "l5.d.action.proposal", offer: "l5.d.action.offer", sign: "l5.d.action.sign", photo: "l7.d.action.photo", onboard: "l5.d.action.onboard", live: "l5.d.action.live" };
function Record({ scene, index, badge, now, reduced, cycle, compact, mid = false }) {
  const id = scene.id;
  const key = `${id}-${cycle}`;
  return (
    <>
      {/* header */}
      <div className="flex items-center gap-3.5 shrink-0">
        <img src={THUMB} alt="" loading="lazy" decoding="async" className={cn("rounded-[12px] object-cover shrink-0", compact ? "h-10 w-10" : "h-12 w-12")} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 min-w-0">
            <h3 className={cn("font-heading font-bold tracking-[-0.03em] leading-none truncate", compact ? "text-[17px]" : "text-[22px]")}>{ADDRESS}</h3>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={badge[1]} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.3, ease: EASE }} className="inline-flex shrink-0" data-testid={`hero-stage-status-${badge[0]}`}>
                <StageBadge stage={badge[0]} label={badge[1]} />
              </motion.span>
            </AnimatePresence>
          </div>
          <p className="mt-1.5 text-[11.5px] text-ink/55 truncate">{t("l4.frame.owner")} · {t("l7.d.owner_role")} · {t("l4.frame.city")} · {t("l4.frame.source")}</p>
        </div>
        {!compact && (
          <span className="inline-flex items-center gap-1.5 h-9 rounded-[10px] bg-lavenderCta text-ink px-3.5 text-[12.5px] font-medium shrink-0" data-testid="hero-record-action">
            <AnimatePresence mode="wait" initial={false}><motion.span key={id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>{t(ACTION[id])}</motion.span></AnimatePresence>
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </span>
        )}
      </div>

      {/* sub-tabs */}
      <div className={cn("shrink-0 flex gap-5 border-b border-hairline overflow-x-auto no-scrollbar", compact ? "mt-3" : "mt-4")} role="tablist" data-testid="hero-record-tabs">
        {TABS.map((k) => {
          const on = scene.tab === k;
          return (
            <span key={k} role="tab" aria-selected={on} className={cn("relative py-2 text-[12.5px] whitespace-nowrap transition-colors duration-300", on ? "text-ink font-medium" : "text-ink/45")} data-testid={`hero-record-tab-${k}`}>
              {t(`l7.d.tab.${k}`)}
              {on && <motion.span layoutId="hero-subtab" className="absolute left-0 right-0 -bottom-px h-[2px] bg-ink" transition={{ duration: 0.4, ease: EASE }} />}
            </span>
          );
        })}
      </div>

      {/* content + activity rail */}
      <div className={cn("flex-1 min-h-0 grid gap-4", compact ? "mt-3 grid-cols-1" : mid ? "mt-4 grid-cols-1" : "mt-4 grid-cols-[minmax(0,1fr)_248px]")}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }} transition={{ duration: 0.5, ease: EASE }} className="min-h-0 flex flex-col" data-testid={`hero-panel-${id}`}>
            <motion.div variants={screen} initial="hidden" animate="show" className="flex-1 min-h-0 flex flex-col gap-3.5">
              {id === "qualify" && <OverviewView reduced={reduced} compact={compact} />}
              {id === "proposal" && <OfferBuilderView reduced={reduced} compact={compact} />}
              {id === "offer" && <OfferLiveView now={now} reduced={reduced} compact={compact} />}
              {id === "sign" && <AgreementView reduced={reduced} compact={compact} />}
              {id === "photo" && <PhotoView now={now} reduced={reduced} compact={compact} />}
              {id === "onboard" && <ReadinessView now={now} reduced={reduced} compact={compact} />}
              {id === "live" && <LiveView reduced={reduced} compact={compact} />}
            </motion.div>
          </motion.div>
        </AnimatePresence>
        {!compact && !mid && <Timeline index={index} now={now} reduced={reduced} />}
      </div>
    </>
  );
}

function Timeline({ index, now, reduced }) {
  const visible = TIMELINE.filter(([, sc, at]) => {
    const si = IDS.indexOf(sc);
    return si < index || (si === index && now >= at);
  }).slice(-7).reverse();
  return (
    <aside className="min-h-0 flex flex-col rounded-[14px] border border-hairline bg-surface p-4" data-testid="hero-timeline">
      <p className="text-[10.5px] uppercase tracking-[0.15em] text-ink/45">{t("l7.d.timeline")}</p>
      <ul className="mt-2.5 relative flex-1 min-h-0">
        <span aria-hidden className="absolute left-[5px] top-2 bottom-2 w-px bg-hairline" />
        <AnimatePresence initial={false}>
          {visible.map(([k, , , time], idx) => (
            <motion.li key={k} layout initial={reduced ? false : { opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.45, ease: EASE, layout: { duration: 0.45, ease: EASE } }} className="relative flex items-start gap-3 py-[7px] pl-0.5" data-testid={`hero-tl-${k}`}>
              <span className={cn("relative mt-[5px] h-[9px] w-[9px] shrink-0 rounded-full ring-[3px] ring-surface", idx === 0 ? "bg-ink" : "bg-ink/25")} />
              <span className="min-w-0 flex-1">
                <span className={cn("block text-[12px] leading-[1.35]", idx === 0 ? "text-ink font-medium" : "text-ink/65")}>{t(`l7.d.tl.${k}`)}</span>
                <span className="block mt-0.5 font-mono text-[10px] text-ink/40 tabular-nums">{time}</span>
              </span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </aside>
  );
}

/* ---------- shared pieces ---------- */
const AiTag = () => <span className="inline-flex items-center rounded-[4px] border border-[rgba(212,150,255,0.5)] bg-[rgba(212,150,255,0.14)] px-1.5 py-px text-[9.5px] font-semibold tracking-[0.04em] text-ink">AI</span>;
const Panel = ({ children, className, ...rest }) => <motion.div variants={item} className={cn("rounded-[14px] border border-hairline bg-surface min-h-0 flex flex-col", className)} {...rest}>{children}</motion.div>;
const PanelLabel = ({ children, right, className }) => (
  <div className={cn("flex items-center justify-between gap-3", className)}>
    <p className="text-[10.5px] uppercase tracking-[0.15em] text-ink/45">{children}</p>
    {right}
  </div>
);
const Cell = ({ label, value, className }) => (
  <div className={cn("min-w-0", className)}>
    <p className="text-[10.5px] text-ink/45 truncate">{label}</p>
    <p className="mt-0.5 text-[13.5px] font-medium text-ink truncate">{value}</p>
  </div>
);

/* estimate · CRM pipeline */
const COLS = [
  ["new", [["Carrer de Mallorca 212", "Lucas Meyer", "2 d"], ["Rue Cler 14", "Chloé Martin", "3 d"]]],
  ["qualified", [["Keizersgracht 210", "Daan de Vries", "5 d"], ["Via Roma 21", "Giulia Rossi", "6 d"]]],
  ["proposal", [["Kings Road 22", "Oliver Hughes", "1 w"], ["Praça das Flores 5", "Ana Ferreira", "1 w"]]],
  ["signed", [["Marina Bay 3", "Sophie Laurent", "2 w"]]],
];
function OurCard({ reduced, compact }) {
  return (
    <motion.div initial={reduced ? false : { height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.6, ease: EASE }} className="overflow-hidden" data-testid="hero-board-new-card">
      <motion.div initial={reduced ? false : { y: -14 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: EASE }} className={cn("rounded-[12px] bg-surface border border-ink shadow-[0_10px_28px_-14px_rgba(21,19,15,0.4)]", compact ? "px-3 py-2.5 mb-1.5" : "p-3.5 mb-2")}>
        <p className="text-[13px] font-medium text-ink truncate">{ADDRESS}</p>
        <p className="mt-0.5 text-[11px] text-ink/55 truncate">{t("l4.frame.owner")} · <span className="text-lavender font-medium">{t("l5.d.just_now")}</span></p>
        <div className="mt-2 flex items-center justify-between gap-2 text-[10.5px]">
          <span className="inline-flex items-center gap-1 text-success font-medium whitespace-nowrap"><Check className="h-3 w-3" strokeWidth={3} /> Google Maps</span>
          <span className="text-ink/60 truncate">3 bd · {t("l7.d.facts.estimate_v")}</span>
        </div>
      </motion.div>
    </motion.div>
  );
}
function PipelineView({ arrived, reduced, compact }) {
  const count = arrived ? 13 : 12;
  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="hero-board">
      <div className="flex items-center justify-between shrink-0">
        <h3 className={cn("font-heading font-bold tracking-[-0.03em] leading-none", compact ? "text-[17px]" : "text-[22px]")}>{t("l4.frame.nav.pipeline")}</h3>
        <div className="flex items-center gap-2 text-[11.5px] text-ink/50">
          {!compact && <span className="inline-flex items-center rounded-full bg-fill px-2.5 h-7 text-ink">All</span>}
          {!compact && <span className="inline-flex items-center rounded-full px-2.5 h-7">Mine</span>}
          <AnimatePresence mode="popLayout" initial={false}><motion.span key={count} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} className="font-mono tabular-nums">{count}</motion.span></AnimatePresence>
        </div>
      </div>
      {compact ? (
        <div className="mt-3 flex-1 min-h-0 rounded-[12px] border border-hairline bg-surface/70 overflow-hidden">
          {COLS.map(([k, cards]) => (
            <div key={k} className="border-b border-hairline last:border-0">
              <div className="flex items-center justify-between px-3 h-8 bg-canvasAlt/60"><span className="text-[11px] font-medium text-ink/60">{t(`l4.h.board.${k}`)}</span><span className="font-mono text-[10.5px] text-ink/40 tabular-nums">{cards.length + (k === "new" && arrived ? 1 : 0)}</span></div>
              <div className="px-2 py-1.5">
                {k === "new" && <AnimatePresence initial={false}>{arrived && <OurCard key="ours" reduced={reduced} compact />}</AnimatePresence>}
                {cards.slice(0, k === "new" ? 2 : 1).map(([addr, owner, age]) => (
                  <div key={addr} className="flex items-center justify-between gap-3 px-1 h-9 text-[12.5px]"><span className="min-w-0 flex items-baseline gap-2"><span className="text-ink/85 truncate font-medium">{addr}</span><span className="text-[10.5px] text-ink/45 truncate">{owner}</span></span><span className="font-mono text-[10.5px] text-ink/45 tabular-nums shrink-0">{age}</span></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 flex-1 min-h-0 grid gap-3 grid-cols-[1.35fr_1fr_1fr_0.9fr]">
          {COLS.map(([k, cards]) => (
            <div key={k} className="rounded-[12px] bg-surface/70 border border-hairline p-2.5 flex flex-col gap-2 min-h-0" data-testid={`hero-board-col-${k}`}>
              <div className="flex items-center justify-between px-1 pb-1"><span className="text-[11px] font-medium text-ink/60">{t(`l4.h.board.${k}`)}</span><span className="font-mono text-[10.5px] text-ink/40 tabular-nums">{cards.length + (k === "new" && arrived ? 1 : 0)}</span></div>
              {k === "new" && <AnimatePresence initial={false}>{arrived && <OurCard key="ours" reduced={reduced} />}</AnimatePresence>}
              {cards.map(([addr, owner, age]) => (
                <div key={addr} className="rounded-[12px] bg-surface border border-hairline p-3.5">
                  <p className="text-[13px] font-medium text-ink/80 truncate">{addr}</p>
                  <div className="mt-1 flex items-center justify-between text-[11px] text-ink/50"><span className="truncate">{owner}</span><span className="font-mono tabular-nums shrink-0">{age}</span></div>
                </div>
              ))}
              <div className="mt-auto flex items-center justify-center h-8 rounded-[10px] border border-dashed border-hairline text-[11px] text-ink/35">+</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* qualify · overview */
const FACTS = ["type", "beds", "baths", "from", "estimate", "source"];
function OverviewView({ reduced, compact }) {
  const reviewed = useAfter(3400, reduced);
  return (
    <>
      <Panel className={cn(compact ? "p-3.5" : "p-4")} data-testid="hero-facts">
        <PanelLabel right={<span className="inline-flex items-center gap-1 text-[10.5px] text-success font-medium"><Check className="h-3 w-3" strokeWidth={3} /> Google Maps</span>}>{t("l7.d.facts")}</PanelLabel>
        <div className={cn("mt-3 grid gap-x-4 gap-y-3", compact ? "grid-cols-2" : "grid-cols-3")}>
          {FACTS.map((k) => <Cell key={k} label={t(`l7.d.facts.${k}`)} value={t(`l7.d.facts.${k}_v`)} />)}
        </div>
      </Panel>
      {!compact && (
        <Panel className="p-4 flex-row items-center gap-3" data-testid="hero-owner-note">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fill text-ink text-[10.5px] font-bold">EC</span>
          <div className="min-w-0">
            <p className="text-[10.5px] text-ink/45">{t("l7.d.note")} · {t("l4.frame.owner")}</p>
            <p className="mt-0.5 text-[12.5px] text-ink truncate">“{t("l7.ph.det_note_v")}”</p>
          </div>
        </Panel>
      )}
      <Panel className={cn("flex-1", compact ? "p-3.5" : "p-4")} data-testid="hero-summary">
        <PanelLabel right={<AiTag />}>{compact ? t("l7.d.summary") : `${t("l7.d.summary")} · ${t("l7.d.summary_tag")}`}</PanelLabel>
        <p className={cn("mt-2.5 text-ink leading-[1.55]", compact ? "text-[12.5px]" : "text-[13.5px]")}>{t("l7.d.summary_text")}</p>
        <div className="mt-auto pt-3 flex items-center justify-between gap-3 text-[12px] min-h-[28px]">
          <span className="inline-flex items-center gap-2 min-w-0"><span className="rounded-full bg-fill px-2 py-0.5 text-[11px] font-medium text-ink shrink-0">{t("l7.d.fit")}</span>{!compact && <span className="text-ink/55 truncate">{t("l7.d.template")}: <span className="text-ink font-medium">{t("l7.d.template_v")}</span></span>}</span>
          <Appear when={reviewed} y={0} className="inline-flex items-center gap-1.5 text-success font-medium whitespace-nowrap shrink-0"><Check className="h-3.5 w-3.5" strokeWidth={3} /> {t("l7.d.tl.reviewed")}</Appear>
        </div>
      </Panel>
    </>
  );
}

/* proposal · offer builder */
const CHIP = { required: "bg-ink text-white", recommended: "bg-[rgba(212,150,255,0.22)] text-ink", optional: "bg-fill text-ink/70" };
const ROWS = [["row1", "required"], ["row2", "recommended"], ["row3", "optional"]];
function OfferBuilderView({ reduced, compact }) {
  const previewTap = useAfter(2200, reduced);
  const sent = useAfter(6000, reduced);
  const delivered = useAfter(6900, reduced);
  return (
    <>
      <Panel className={cn(compact ? "p-3.5" : "p-4")} data-testid="hero-proposal-card">
        <PanelLabel right={<span className="text-[11px] text-ink/45">{t("l7.d.offer.title")} · {t("l7.d.template_v")}</span>}>{t("l7.d.offer.services")}</PanelLabel>
        <ul className="mt-1.5">
          {ROWS.map(([k, kind]) => (
            <motion.li key={k} variants={item} className={cn("flex items-center justify-between gap-3 border-b border-hairline last:border-0", compact ? "py-2.5 text-[12.5px]" : "py-3 text-[13.5px]")}>
              <span className="min-w-0">
                <span className="flex items-center gap-2"><span className="text-ink">{t(`l4.frame.proposal.${k}`)}</span><span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0", CHIP[kind])}>{t(`chip.${kind}`)}</span></span>
                {!compact && <span className="block mt-0.5 text-[11.5px] text-ink/50">{t(`l5.d.proposal.${k}_d`)}</span>}
              </span>
              <span className={cn("shrink-0 tabular-nums whitespace-nowrap", kind === "optional" ? "text-ink/50" : "text-ink font-medium")}>{t(`l4.frame.proposal.${k}_v`)}</span>
            </motion.li>
          ))}
        </ul>
        <motion.div variants={item} className="mt-1.5 flex items-center h-8 text-[12px] text-ink/50"><Plus className="h-3.5 w-3.5 mr-1.5" /> {t("l5.d.proposal.add")}</motion.div>
      </Panel>
      <Panel className={cn("flex-1 justify-between", compact ? "p-3.5" : "p-4")} data-testid="hero-proposal-send">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.15em] text-ink/45">{t("l7.d.offer.preview")}</p>
            <p className="mt-1 font-mono text-[12px] text-ink truncate">{t("l7.d.offer.preview_v")}</p>
            <p className="mt-1 text-[11.5px] text-ink/50">{t("l7.d.offer.valid")}</p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {!compact && (
              <motion.span animate={previewTap && !sent ? { scale: [1, 0.96, 1] } : { scale: 1 }} transition={{ duration: 0.4, ease: EASE }} className={cn("relative overflow-hidden inline-flex items-center gap-1.5 h-9 rounded-[10px] border px-3 text-[12.5px] font-medium transition-colors duration-500", previewTap && !sent ? "border-ink/30 bg-fill text-ink" : "border-hairline bg-surface text-ink/80")} data-testid="hero-preview-button">
                <Eye className="h-3.5 w-3.5" strokeWidth={1.9} /> {t("l7.d.preview.cta")}
                <TapRipple when={previewTap && !sent} reduced={reduced} dark />
              </motion.span>
            )}
            <AnimatePresence mode="wait" initial={false}>
              {sent ? (
                <motion.span key="sent" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 h-9 text-[13px] text-ink font-medium"><Tick reduced={reduced} size={18} /> {t("l7.d.offer.sent")}</motion.span>
              ) : (
                <motion.span key="send" exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.18 } }} className="relative overflow-hidden inline-flex items-center gap-2 h-9 rounded-[10px] bg-ink text-white px-3.5 text-[12.5px] font-medium"><Mail className="h-3.5 w-3.5" /> {t("l7.d.offer.send")}</motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="mt-3 h-6">
          <Appear when={delivered} y={0} className="inline-flex items-center gap-2 text-[11.5px] text-ink/60"><span className="h-1.5 w-1.5 rounded-full bg-success shrink-0" /> {t("l7.d.offer.delivered")}</Appear>
        </div>
      </Panel>
    </>
  );
}

/* proposal · fullscreen preview of the owner page — exactly what Emma will see, in Northstay's brand */
const PREVIEW_FACTS = [["l7.d.preview.est_short", "l5.ph.est_value", true], ["l5.ph.offer_fee", "l5.ph.offer_fee_v"], ["l5.ph.offer_net_short", "l5.ph.offer_net_v"]];
const PREVIEW_SUMMARY = [["l5.ph.offer_fee", "l5.ph.offer_plan_pct"], ["l5.ph.offer_one_time", "l7.d.preview.zero"], ["l5.ph.offer_net_short", "l5.ph.offer_net_v"]];
function OfferPreview({ reduced, compact, mid }) {
  return (
    <motion.div initial={reduced ? false : { opacity: 0, scale: 0.985, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.99, y: 6, transition: { duration: 0.35, ease: [0.4, 0, 1, 1] } }} transition={{ duration: 0.55, ease: EASE }} className="absolute inset-0 z-20 bg-canvas flex flex-col will-change-transform" data-testid="hero-offer-preview">
      {/* preview chrome */}
      <div className={cn("shrink-0 flex items-center justify-between gap-3 border-b border-hairline bg-surface/90", compact ? "h-10 px-3" : "h-11 px-4")}>
        <span className="inline-flex items-center gap-2 text-[12px] font-medium text-ink/70 min-w-0"><Eye className="h-3.5 w-3.5 text-ink/50 shrink-0" strokeWidth={1.9} /><span className="truncate">{t("l7.d.preview.title")}</span></span>
        {!compact && <span className="inline-flex items-center gap-1.5 h-7 rounded-full bg-fill px-3 font-mono text-[10.5px] text-ink/60"><Lock className="h-3 w-3" strokeWidth={2.2} />{t("l7.d.offer.preview_v")}</span>}
        <span className="inline-flex items-center gap-2 shrink-0">
          {!compact && (
            <span className="inline-flex items-center rounded-full border border-hairline bg-canvas p-0.5 text-[11px]" aria-hidden>
              <span className="inline-flex items-center gap-1 h-6 rounded-full bg-ink text-white px-2.5"><Monitor className="h-3 w-3" /> {t("l7.d.preview.desktop")}</span>
              <span className="inline-flex items-center gap-1 h-6 rounded-full px-2.5 text-ink/55"><Smartphone className="h-3 w-3" /> {t("l7.d.preview.mobile")}</span>
            </span>
          )}
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-fill text-ink/70"><X className="h-3.5 w-3.5" strokeWidth={2.2} /></span>
        </span>
      </div>

      {/* the owner page */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col" data-testid="hero-owner-page">
        <div className={cn("shrink-0 flex items-center justify-between gap-4 border-b border-hairline", compact ? "h-10 px-4" : "h-12 px-8")}>
          <span className="inline-flex items-center gap-2"><TenantMark /><BrandName className="font-heading font-bold text-[14px] tracking-[-0.02em] text-ink max-w-[140px]" /></span>
          <span className="text-[11.5px] text-ink/50 truncate">{t("l5.ph.offer_meta")}</span>
        </div>
        <motion.div variants={screen} initial="hidden" animate="show" className={cn("flex-1 min-h-0 grid", compact ? "px-4 pt-4 pb-4 grid-cols-1 content-start gap-3" : mid ? "px-7 pt-6 pb-6 grid-cols-[minmax(0,1fr)_300px] gap-6" : "px-8 pt-7 pb-7 grid-cols-[minmax(0,1.1fr)_360px] gap-10")}>
          <div className="min-w-0">
            <motion.p variants={item} className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l5.ph.offer_eyebrow")}</motion.p>
            <motion.h3 variants={item} className={cn("mt-2 font-heading font-bold tracking-[-0.03em] text-ink text-balance", compact ? "text-[18px] leading-[1.15]" : mid ? "text-[24px] leading-[1.1] max-w-[22ch]" : "text-[30px] leading-[1.08] max-w-[22ch]")}>{t("l5.ph.offer_title")}</motion.h3>
            <motion.div variants={item} className={cn("relative mt-4 rounded-[16px] overflow-hidden", compact ? "h-[110px]" : mid ? "h-[170px]" : "h-[216px]")}>
              <img src="/img/interior-openplan.webp" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
              <span className="absolute left-4 bottom-3.5 text-white"><span className="block text-[10px] uppercase tracking-[0.14em] text-white/70">Baixa · {CITY}</span><span className="block mt-0.5 font-heading font-bold text-[18px] leading-none tracking-[-0.02em]">{ADDRESS}</span></span>
            </motion.div>
            {compact ? (
              <motion.div variants={item} className="mt-3 rounded-[14px] text-white px-3.5 py-3 flex items-center justify-between gap-3" style={{ background: BRAND_DEEP }}>
                <p className="text-[9.5px] uppercase tracking-[0.16em]" style={{ color: BRAND_LIGHT }}>{t("l7.d.preview.est_short")}</p>
                <p className="font-heading font-bold text-[15px] leading-none tabular-nums">{t("l5.ph.est_value")}</p>
              </motion.div>
            ) : (
              <motion.div variants={item} className="mt-4 grid grid-cols-3 gap-3">
                {PREVIEW_FACTS.map(([lk, vk, dark]) => (
                  <div key={lk} className={cn("rounded-[14px] p-3.5 min-w-0", dark ? "text-white" : "border border-hairline bg-surface text-ink")} style={dark ? { background: BRAND_DEEP } : undefined}>
                    <p className={cn("text-[9.5px] uppercase tracking-[0.16em] truncate", !dark && "text-ink/45")} style={dark ? { color: BRAND_LIGHT } : undefined}>{t(lk)}</p>
                    <p className={cn("mt-1.5 font-heading font-bold leading-tight tabular-nums truncate", mid ? "text-[14px]" : "text-[16px]")}>{t(vk)}</p>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
          <motion.aside variants={item} className={cn("rounded-[18px] border border-hairline bg-surface flex flex-col shadow-[0_24px_60px_-40px_rgba(21,19,15,0.35)]", compact ? "p-3.5" : "p-5")} data-testid="hero-owner-page-plan">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><p className="text-[9.5px] uppercase tracking-[0.18em] text-ink/45">{t("l5.ph.offer_plan")}</p><p className="mt-1 font-heading font-bold text-[18px] tracking-[-0.02em] text-ink">{t("l5.ph.offer_plan_name")}</p></div>
              <div className="text-right shrink-0"><p className="font-heading font-bold text-[22px] leading-none text-ink">{t("l5.ph.offer_plan_pct")}</p><p className="text-[9.5px] text-ink/50 mt-0.5">{t("l5.ph.offer_of_revenue")}</p></div>
            </div>
            <ul className={cn("mt-3 pt-3 border-t border-hairline", compact ? "space-y-1" : "space-y-1.5")}>
              {["inc1", "inc2", "inc3"].map((k) => (
                <li key={k} className="flex items-center gap-2 text-[11.5px] text-ink"><Check className="h-3 w-3 text-success shrink-0" strokeWidth={3} /><span className="truncate">{t(`l5.ph.offer_${k}`)}</span></li>
              ))}
            </ul>
            {!compact && (
              <div className="mt-3 pt-3 border-t border-hairline">
                <p className="text-[9.5px] uppercase tracking-[0.18em] text-ink/45">{t("l5.ph.offer_extras")}</p>
                <div className="mt-2 space-y-2">
                  {[["l5.ph.offer_photo", "l5.ph.offer_photo_p"], ["l5.ph.offer_styling", "l5.ph.offer_styling_p"]].map(([n, p]) => (
                    <div key={n} className="flex items-center justify-between gap-3 text-[11.5px]">
                      <span className="min-w-0"><span className="block text-ink truncate">{t(n)}</span><span className="block text-[10px] text-ink/50">{t(p)}</span></span>
                      <span className="inline-flex items-center gap-1 h-6 rounded-full border border-hairline px-2 text-[10.5px] text-ink shrink-0"><Plus className="h-3 w-3" /> {t("chip.add")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-auto pt-4">
              {!compact && (
                <dl className="mb-3 rounded-[12px] bg-canvasAlt px-3.5 py-3 space-y-1.5 text-[11.5px]" data-testid="hero-owner-page-summary">
                  {PREVIEW_SUMMARY.map(([lk, vk], i) => (
                    <div key={lk} className={cn("flex items-center justify-between gap-3", i === PREVIEW_SUMMARY.length - 1 && "pt-1.5 border-t border-hairline")}>
                      <dt className="text-ink/55 truncate">{t(lk)}</dt>
                      <dd className={cn("tabular-nums shrink-0", i === PREVIEW_SUMMARY.length - 1 ? "font-heading font-bold text-[14px] text-ink" : "font-medium text-ink")}>{t(vk)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <span className="flex h-10 items-center justify-center gap-2 rounded-full text-[12.5px] font-medium" style={{ background: BRAND, color: BRAND_FG }}>{t("l5.ph.offer_accept")} <ArrowRight className="h-3.5 w-3.5" /></span>
              <p className="mt-2 text-center text-[9.5px] text-ink/40">{t("brand.powered_by")}</p>
            </div>
          </motion.aside>
        </motion.div>
      </div>
    </motion.div>
  );
}

/* offer · owner activity, live */
function OfferLiveView({ now, reduced, compact }) {
  const added = now >= 4500;
  const accepted = now >= 7100;
  return (
    <>
      <Panel className={cn(compact ? "p-3.5" : "p-4")} data-testid="hero-activity-card">
        <PanelLabel right={<span className="inline-flex items-center gap-1.5 rounded-full bg-fill px-2.5 h-6 text-[11px] text-ink"><span className="h-1.5 w-1.5 rounded-full bg-success pulse-dot" /> {t("l7.d.offer.live")}</span>}>{t("l7.d.offer.choices")}</PanelLabel>
        <ul className={cn("mt-1.5", compact ? "text-[12.5px]" : "text-[13.5px]")}>
          {[["row1", true], ["row2", added], ["row3", false]].map(([k, on]) => (
            <li key={k} className={cn("flex items-center justify-between gap-3 border-b border-hairline last:border-0 transition-opacity duration-500", compact ? "py-2.5" : "py-3", !on && "opacity-45")} data-testid={`hero-choice-${k}`} data-on={on ? "true" : "false"}>
              <span className="inline-flex items-center gap-2.5 text-ink min-w-0">
                <span className="relative h-[18px] w-[18px] shrink-0"><span className="absolute inset-0 rounded-full border border-hairline" /><AnimatePresence>{on && <Tick key="t" reduced={reduced} size={18} className="absolute inset-0" />}</AnimatePresence></span>
                <span className="truncate">{t(`l4.frame.proposal.${k}`)}</span>
              </span>
              <span className="shrink-0 tabular-nums whitespace-nowrap text-ink">{on ? t(`l4.frame.proposal.${k}_v`) : "—"}</span>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel className={cn("flex-1 justify-between", compact ? "p-3.5" : "p-4")}>
        {!compact && (
          <div className="grid grid-cols-3 gap-x-4 pb-3 border-b border-hairline">
            <Cell label={t("l7.d.offer.preview")} value={t("l7.d.offer.preview_short")} />
            <Cell label={t("l7.d.offer.device")} value={t("l7.d.offer.device_v")} />
            <Cell label={t("l7.d.offer.time")} value={t("l7.d.offer.time_v")} />
          </div>
        )}
        <div className={cn("flex items-center justify-between gap-3", !compact && "mt-3")}>
          <span className="text-[12.5px] text-ink/55">{t("l7.d.offer.net")}</span>
          <AnimatePresence mode="wait" initial={false}><motion.span key={added ? "a" : "b"} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} className="font-heading font-bold text-[18px] text-ink tabular-nums whitespace-nowrap">{added ? t("l5.ph.offer_net_v2") : t("l5.ph.offer_net_v")}</motion.span></AnimatePresence>
        </div>
        <div className="mt-3 h-10">
          <Appear when={accepted} y={0} className="flex items-center justify-center gap-2 h-10 rounded-[10px] bg-successBg text-success text-[12.5px] font-medium" data-testid="hero-offer-accepted"><Check className="h-3.5 w-3.5" strokeWidth={3} /> {t("l7.d.offer.accepted")}</Appear>
        </div>
      </Panel>
    </>
  );
}

/* sign · agreement */
const SIG_D = "M14 50 C 20 30, 28 16, 34 26 C 40 38, 38 54, 32 60 C 28 64, 28 52, 38 40 C 50 26, 58 22, 62 34 C 66 46, 58 56, 66 54 C 76 52, 80 36, 88 32 C 96 28, 96 42, 92 50 C 88 58, 96 58, 104 44 C 112 30, 120 26, 126 34 C 132 44, 130 56, 140 50 C 150 44, 156 28, 168 30 C 180 32, 174 50, 186 48 C 198 46, 206 30, 218 32 C 230 34, 228 50, 242 44";
const TERMS = [["row1", "l4.frame.proposal.row1", "l4.frame.proposal.row1_v"], ["row2", "l4.frame.proposal.row2", "l4.frame.proposal.row2_v"], ["term", "l5.d.sign.term", "l6.h.sign.term_short"], ["start", "l5.d.sign.start", "l5.d.sign.start_v"], ["payout", "l6.h.sign.payout", "l6.h.sign.payout_v"], ["notice", "l6.h.sign.notice", "l6.h.sign.notice_v"]];
function AgreementView({ reduced, compact }) {
  const signed = useAfter(2600, reduced);
  return (
    <div className={cn("flex-1 min-h-0 grid gap-3.5", compact ? "grid-cols-1" : "grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]")} data-testid="hero-agreement-card">
      <Panel className={cn(compact ? "p-3.5" : "p-4")}>
        <PanelLabel right={<span className="text-[11px] text-ink/45">{t("l7.d.sign.title")}</span>}>{t("l7.d.sign.terms")}</PanelLabel>
        <ul className="mt-1">
          {TERMS.map(([k, lk, vk]) => (
            <motion.li key={k} variants={item} className={cn("flex items-center justify-between gap-3 border-b border-hairline last:border-0", compact ? "h-9 text-[12px]" : "h-10 text-[12.5px]")}>
              <span className="text-ink/60 truncate">{t(lk)}</span>
              <span className="text-ink shrink-0 tabular-nums">{t(vk)}</span>
            </motion.li>
          ))}
        </ul>
      </Panel>
      <Panel className={cn(compact ? "p-3.5" : "p-4")}>
        <PanelLabel right={<span className="text-[11px] text-ink/45">{t("l5.d.sign.esign")}</span>}>{t("l7.d.sign.signature")}</PanelLabel>
        <div className={cn("mt-3 relative rounded-[12px] border border-dashed border-ink/20 bg-canvas overflow-hidden", compact ? "h-[84px]" : "h-[104px]")}>
          <svg viewBox="0 0 256 76" className="absolute inset-0 h-full w-full" aria-hidden>
            <motion.path d={SIG_D} fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" initial={reduced ? false : { pathLength: 0, opacity: 0.5 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.4, ease: [0.45, 0, 0.2, 1], delay: 0.5 }} />
          </svg>
          <span aria-hidden className="absolute left-3.5 right-3.5 bottom-3 h-px bg-ink/15" />
        </div>
        <div className="mt-auto pt-3 min-h-[44px]">
          <AnimatePresence mode="wait" initial={false}>
            {signed ? (
              <motion.div key="ok" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
                <span className="inline-flex items-center gap-2 text-[13px] text-ink font-medium"><Tick reduced={reduced} size={18} /> {t("l7.d.sign.signed")}</span>
                {!compact && (
                  <dl className="mt-2.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-1 text-[11px]">
                    {[["l6.h.sign.ts", "3 Jun · 14:32:07"], ["l6.h.sign.ip", "84.212.•••.••"], ["l6.h.sign.doc", "v2 · SHA-256 9f3c…e1a7"]].map(([k, v], i) => (
                      <React.Fragment key={k}><motion.dt initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 + i * 0.12 }} className="text-ink/45">{t(k)}</motion.dt><motion.dd initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 + i * 0.12 }} className="font-mono text-ink/80 truncate">{v}</motion.dd></React.Fragment>
                    ))}
                  </dl>
                )}
              </motion.div>
            ) : (
              <motion.span key="wait" exit={{ opacity: 0 }} className="inline-flex items-center gap-2.5 text-[12.5px] text-ink/55"><span className="h-2 w-2 rounded-full bg-ink/25 pulse-dot" /> {t("l7.d.sign.awaiting")}</motion.span>
            )}
          </AnimatePresence>
        </div>
      </Panel>
    </div>
  );
}

/* photo · the shoot is booked — the record and automations follow */
const PHOTO_AUTO = [["n1", 5600], ["n2", 6400], ["n3", 7200]];
function PhotoView({ now, reduced, compact }) {
  return (
    <>
      <Panel className={cn(compact ? "p-3.5" : "p-4")} data-testid="hero-photo-card">
        <PanelLabel right={<span className="inline-flex items-center gap-1.5 text-[10.5px] text-success font-medium"><Check className="h-3 w-3" strokeWidth={3} /> {t("l7.d.photo.status")}</span>}>{t("l7.d.photo.title")}</PanelLabel>
        <motion.div variants={item} className={cn("mt-3 flex items-center gap-3.5 rounded-[12px] bg-ink text-white", compact ? "p-3" : "p-3.5")}>
          <span className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-[10px] bg-white/10">
            <span className="text-[9px] uppercase tracking-[0.12em] text-white/60 leading-none">Jun</span>
            <span className="mt-0.5 font-heading font-bold text-[18px] leading-none tabular-nums">12</span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] font-medium truncate">{t("l7.d.photo.when")}</p>
            <p className="mt-0.5 text-[11.5px] text-white/60 truncate">{t("l7.d.photo.who_v")} · {t("l7.d.photo.dur_v")}</p>
          </div>
          <Camera className="h-4 w-4 text-white/60 shrink-0" strokeWidth={1.8} />
        </motion.div>
        {!compact && (
          <div className="mt-3 grid grid-cols-3 gap-x-4">
            <Cell label={t("l7.d.photo.who")} value={t("l7.d.photo.who_v")} />
            <Cell label={t("l7.d.photo.dur")} value={t("l7.d.photo.dur_v")} />
            <Cell label={t("l7.d.photo.access")} value={t("l7.d.photo.access_v")} />
          </div>
        )}
      </Panel>
      <Panel className={cn("flex-1", compact ? "p-3.5" : "p-4")} data-testid="hero-photo-automations">
        <PanelLabel right={<AiTag />}>{t("l7.d.photo.next")}</PanelLabel>
        <ul className="mt-1 flex-1 flex flex-col justify-around">
          {PHOTO_AUTO.map(([k, at]) => {
            const on = now >= at;
            return (
              <li key={k} className={cn("flex items-center gap-3", compact ? "h-9 text-[12.5px]" : "h-10 text-[13px]")} data-testid={`hero-photo-auto-${k}`} data-on={on ? "true" : "false"}>
                <span className="relative h-[18px] w-[18px] shrink-0">
                  <span className="absolute inset-0 rounded-full border border-hairline" />
                  <AnimatePresence>{on && <Tick key="t" reduced={reduced} size={18} className="absolute inset-0" />}</AnimatePresence>
                </span>
                <motion.span animate={{ opacity: on ? 1 : 0.45 }} transition={{ duration: 0.4 }} className="flex-1 truncate text-ink">{t(`l7.d.photo.${k}`)}</motion.span>
              </li>
            );
          })}
        </ul>
      </Panel>
    </>
  );
}

/* onboard · launch readiness — mirrors Emma's phone */
const READY_ITEMS = [["i1", 0, "owner"], ["i4", 0, "owner"], ["i2", 3000, "owner"], ["i3", 4800, "owner"], ["i5", 5200, "internal"]];
function ReadinessView({ now, reduced, compact }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const doneCount = READY_ITEMS.filter(([, at]) => now >= at).length;
  const pct = Math.round((doneCount / READY_ITEMS.length) * 100);
  const ready = doneCount === READY_ITEMS.length;
  return (
    <div className={cn("flex-1 min-h-0 grid gap-3.5", compact ? "grid-cols-1" : "grid-cols-[minmax(0,0.85fr)_minmax(0,1.25fr)]")} data-testid="hero-readiness-card">
      <Panel className={cn("items-center justify-center text-center", compact ? "p-3.5 flex-row gap-4" : "p-5")}>
        <div className="relative shrink-0" style={{ height: compact ? 84 : 132, width: compact ? 84 : 132 }} data-testid="hero-stage-ring">
          <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90">
            <circle cx="32" cy="32" r={r} fill="none" stroke="var(--hairline)" strokeWidth="5" />
            <motion.circle cx="32" cy="32" r={r} fill="none" stroke="var(--ink)" strokeWidth="5" strokeLinecap="round" strokeDasharray={c} initial={reduced ? false : { strokeDashoffset: c * 0.6 }} animate={{ strokeDashoffset: c * (1 - pct / 100) }} transition={{ duration: 1.1, ease: EASE }} />
          </svg>
          <span className={cn("absolute inset-0 flex items-center justify-center font-heading font-bold tabular-nums text-ink", compact ? "text-[17px]" : "text-[24px]")}>{pct}%</span>
        </div>
        <div className={compact ? "text-left" : "mt-4"}>
          <p className="text-[10.5px] uppercase tracking-[0.15em] text-ink/45">{t("l7.d.onb.title")}</p>
          <p className={cn("mt-1 text-ink", compact ? "text-[12.5px]" : "text-[13px]")}>{t("l5.d.ready.count", { n: doneCount })}</p>
          <AnimatePresence mode="wait" initial={false}>
            <motion.p key={ready ? "r" : "b"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={cn("mt-1 text-[12px]", ready ? "text-success font-medium" : "text-ink/50")}>{ready ? t("l5.d.ready.zero") : t("l5.d.ready.blockers", { n: READY_ITEMS.length - doneCount })}</motion.p>
          </AnimatePresence>
        </div>
      </Panel>
      <Panel className={cn(compact ? "p-3.5" : "p-4")}>
        <PanelLabel right={<span className="text-[11px] text-ink/45">{doneCount}/5</span>}>{t("l7.d.onb.checklist")}</PanelLabel>
        <ul className="mt-1 flex-1 flex flex-col justify-around">
          {READY_ITEMS.map(([k, at, who]) => {
            const on = now >= at;
            return (
              <li key={k} className={cn("flex items-center gap-3", compact ? "h-9 text-[12.5px]" : "h-10 text-[13px]")}>
                <span className="relative h-[18px] w-[18px] shrink-0">
                  <span className="absolute inset-0 rounded-full border border-hairline" />
                  <AnimatePresence>{on && <Tick key="t" reduced={reduced} size={18} className="absolute inset-0" />}</AnimatePresence>
                </span>
                <motion.span animate={{ opacity: on ? 1 : 0.45 }} className="flex-1 truncate text-ink">{t(`l4.frame.ready.${k}`)}</motion.span>
                {!compact && <span className="text-[10.5px] text-ink/40 shrink-0">{t(`l5.d.ready.${who}`)}</span>}
              </li>
            );
          })}
        </ul>
      </Panel>
    </div>
  );
}

/* live · handoff */
const HANDOFF_FIELDS = ["f1", "f2", "f3", "f4", "f5", "f6"];
function LiveView({ reduced, compact }) {
  const created = useAfter(1500, reduced);
  return (
    <>
      <Panel className={cn(compact ? "p-3.5" : "p-4")} data-testid="hero-handoff-card">
        <PanelLabel>{t("l7.d.live.handoff")}</PanelLabel>
        <motion.div variants={item} className={cn("mt-3 flex items-center justify-between gap-3 rounded-[12px] bg-ink text-white px-4", compact ? "h-12" : "h-14")}>
          <span className="inline-flex items-center gap-2 min-w-0 text-[12.5px]"><span className="truncate">{t("l7.d.live.record")}</span><ArrowRight className="h-3.5 w-3.5 text-white/40 shrink-0" /></span>
          <span className="inline-flex items-center gap-2.5 shrink-0"><BrandLogo id="guesty" scale={0.8} />{created && <Tick reduced={reduced} size={18} />}</span>
        </motion.div>
        <ul className={cn("mt-3 grid", compact ? "grid-cols-2 gap-x-3" : "grid-cols-3 gap-x-4")}>
          {HANDOFF_FIELDS.map((k, i) => (
            <motion.li key={k} initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.14 }} className={cn("flex items-center justify-between gap-2 text-ink border-b border-hairline", compact ? "h-7 text-[11px]" : "h-8 text-[12px]")}>
              <span className="truncate">{t(`l5.jr.pms.${k}`)}</span><Check className="h-3 w-3 text-success shrink-0" strokeWidth={3} />
            </motion.li>
          ))}
        </ul>
        <div className="mt-2.5 h-6">
          <Appear when={created} y={0} className="inline-flex items-center gap-2 text-[12px] text-ink font-medium"><Check className="h-3.5 w-3.5 text-success" strokeWidth={3} /> {t("l7.d.live.created")}</Appear>
        </div>
      </Panel>
      <Panel className={cn("flex-1", compact ? "p-3.5" : "p-4")}>
        <PanelLabel>{t("l7.d.live.channels")}</PanelLabel>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {["airbnb", "booking"].map((cid, idx) => (
            <motion.span key={cid} initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.2 + idx * 0.4, duration: 0.45, ease: EASE }} className={cn("rounded-[12px] border border-hairline bg-canvas flex items-center justify-center text-ink/85", compact ? "h-11" : "h-12")} data-testid={`hero-stage-channel-${cid}`}>
              <BrandLogo id={cid} scale={0.66} />
            </motion.span>
          ))}
        </div>
        <motion.div initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.4, duration: 0.45 }} className="mt-auto pt-3 grid grid-cols-2 gap-2">
          {["kpi1", "kpi2"].map((k) => (
            <div key={k} className="rounded-[12px] bg-canvasAlt px-3.5 py-3">
              <p className="text-[10.5px] text-ink/50">{t(`l7.d.live.${k}`)}</p>
              <p className={cn("font-heading font-bold text-ink leading-none mt-1", compact ? "text-[17px]" : "text-[20px]")}>{t(`l7.d.live.${k}_v`)}</p>
            </div>
          ))}
        </motion.div>
      </Panel>
    </>
  );
}

/* ======================================================================
   Phone — the owner's surface (tenant-branded)
====================================================================== */
/* How each phone screen arrives: `page` (the website, dims and recedes when the sheet covers it),
   `sheet` (Listed rises from the bottom over the site), `push` (next page in the same flow), `fade`. */
const ENTER = { entry: "page", details: "push", estimate: "push", sign: "push" };
const IOS = [0.32, 0.72, 0, 1];
const phoneScreen = {
  hidden: (m) => (m === "push" ? { opacity: 0, x: 72, y: 0, scale: 1 } : m === "sheet" ? { opacity: 1, x: 0, y: "100%", scale: 1 } : { opacity: 0, x: 0, y: 14, scale: 1 }),
  show: (m) => ({ opacity: 1, x: 0, y: 0, scale: 1, transition: m === "sheet" ? { duration: 0.78, ease: IOS, staggerChildren: 0.06, delayChildren: 0.5 } : m === "push" ? { duration: 0.6, ease: IOS, staggerChildren: 0.05, delayChildren: 0.18 } : { duration: 0.55, ease: EASE, delay: 0.06, staggerChildren: 0.06, delayChildren: 0.14 } }),
  exit: (m) =>
    m === "push"
      ? { opacity: 0, x: -48, transition: { duration: 0.36, ease: [0.4, 0, 1, 1] } }
      : m === "page"
        ? { opacity: 0, x: -48, transition: { duration: 0.36, ease: [0.4, 0, 1, 1] } }
        : m === "sheet"
          ? { opacity: 0.4, scale: 0.96, y: -6, transition: { duration: 0.6, ease: IOS } }
          : { opacity: 0, y: -10, transition: { duration: 0.26, ease: [0.4, 0, 1, 1] } },
};
const CLOCK = { site: "09:00", capture: "09:01", details: "09:01", estimate: "09:02", offer: "09:16", sign: "14:31", photo: "14:36", onboard: "17:02", live: "18:40" };
/* Titanium body: a soft diagonal sheen, brighter on the lit edge. */
const PHONE_BODY = "linear-gradient(152deg, #6A655D 0%, #3B3833 18%, #1E1C19 46%, #262320 72%, #4C4841 100%)"; // warm titanium — DigiHome's ink family
const STATUS_H = 52;
const HOME_H = 18;
function Phone({ scene, reduced, cycle, started = true }) {
  const scr = SCREEN[scene.id];
  const edge = scr === "entry";
  const lightStatus = scene.id === "site";
  const b = useDemoBrand();
  // When the brand changes (a visitor's site loads), a light band sweeps across the screen once.
  const sig = `${b.name}|${b.color}|${b.logoUrl || ""}`;
  const prevSig = useRef(sig);
  const [sweep, setSweep] = useState(0);
  useEffect(() => {
    if (prevSig.current === sig) return undefined;
    prevSig.current = sig;
    setSweep((s) => s + 1);
    const id = setTimeout(() => setSweep(0), 1300);
    return () => clearTimeout(id);
  }, [sig]);
  return (
    <div className="relative h-full w-full select-none" data-testid="hero-phone" data-screen={scr}>
      {/* contact shadow on the canvas */}
      <span aria-hidden className="pointer-events-none hidden lg:block absolute left-[8%] right-[8%] -bottom-7 h-12 rounded-[50%] bg-ink/40" style={{ filter: "blur(24px)" }} />
      <div className="relative h-full w-full rounded-[54px] p-[8px] shadow-[0_60px_120px_-42px_rgba(21,19,15,0.6),0_28px_56px_-30px_rgba(21,19,15,0.45)]" style={{ background: PHONE_BODY }}>
        {/* rim highlight + inner edge */}
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[54px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.32),inset_0_0_0_2.5px_rgba(0,0,0,0.35)]" />
        <span aria-hidden className="pointer-events-none absolute inset-[2px] rounded-[52px] shadow-[inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.5)]" />
        {/* side keys */}
        <span aria-hidden className="absolute -left-[3px] top-[116px] h-[26px] w-[3px] rounded-l-[2px] bg-[#34312B] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" />
        <span aria-hidden className="absolute -left-[3px] top-[164px] h-[56px] w-[3px] rounded-l-[2px] bg-[#34312B] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" />
        <span aria-hidden className="absolute -left-[3px] top-[230px] h-[56px] w-[3px] rounded-l-[2px] bg-[#34312B] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" />
        <span aria-hidden className="absolute -right-[3px] top-[186px] h-[92px] w-[3px] rounded-r-[2px] bg-[#34312B] shadow-[inset_-1px_0_0_rgba(255,255,255,0.2)]" />
        {/* black bezel */}
        <div className="relative h-full w-full rounded-[46px] bg-[#040405] p-[3px]">
          {/* clip-path + contain:paint — Chrome does not always honour overflow:hidden + border-radius for composited
              children inside a 3D-tilted parent; the site page could then bleed past the bottom of the frame. */}
          <div className="relative h-full w-full rounded-[43px] bg-canvas overflow-hidden" style={{ transform: "translateZ(0)", clipPath: "inset(0 round 43px)", WebkitClipPath: "inset(0 round 43px)", contain: "paint", isolation: "isolate" }} data-testid="hero-phone-screen">
            {/* screens — edge to edge; most pad for the status bar and home indicator, the website runs under them */}
            <div className="absolute inset-0">
              <AnimatePresence mode="popLayout" initial={false} custom={ENTER[scr] || "fade"}>
                <motion.div key={`${scr}-${cycle}`} custom={ENTER[scr] || "fade"} variants={phoneScreen} initial="hidden" animate="show" exit="exit" className="absolute inset-0 flex flex-col will-change-transform bg-canvas" style={edge ? undefined : { paddingTop: STATUS_H, paddingBottom: HOME_H }} data-testid={`hero-phone-${scr}`} data-enter={ENTER[scr] || "fade"}>
                  {scr === "entry" && <ScreenEntry phase={scene.id} reduced={reduced} live={started} />}
                  {scr === "details" && <ScreenDetails reduced={reduced} />}
                  {scr === "estimate" && <ScreenEstimate reduced={reduced} />}
                  {scr === "offer" && <ScreenOffer reduced={reduced} />}
                  {scr === "sign" && <ScreenSign reduced={reduced} />}
                  {scr === "photo" && <ScreenPhoto reduced={reduced} />}
                  {scr === "onboard" && <ScreenOnboard reduced={reduced} />}
                  {scr === "live" && <ScreenLive reduced={reduced} />}
                </motion.div>
              </AnimatePresence>
            </div>
            {/* status bar + dynamic island — light over the website's photo, ink everywhere else */}
            <div className={cn("absolute inset-x-0 top-0 z-40 flex items-end justify-between px-7 pb-[9px] text-[12.5px] font-semibold transition-colors duration-500 ease-editorial", lightStatus ? "text-white" : "text-ink")} style={{ height: STATUS_H }} data-testid="phone-status-bar" data-light={lightStatus ? "true" : "false"}>
              <span className="tabular-nums tracking-[-0.01em]">{CLOCK[scene.id] || CLOCK[scr]}</span>
              <span aria-hidden className="absolute left-1/2 top-[11px] -translate-x-1/2 h-[26px] w-[92px] rounded-full bg-[#050506] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]">
                <span className="absolute right-[9px] top-1/2 -translate-y-1/2 h-[9px] w-[9px] rounded-full bg-[#0f1a24] ring-1 ring-white/10">
                  <span className="absolute left-[2px] top-[1.5px] h-[3px] w-[3px] rounded-full bg-[#2a4f6a] opacity-90" />
                </span>
              </span>
              <span className="inline-flex items-center gap-[5px]" aria-hidden>
                <span className="flex items-end gap-[1.5px]">{[4, 6, 8, 10].map((h) => <span key={h} className="w-[3px] rounded-[1px] bg-current" style={{ height: h }} />)}</span>
                <Wifi className="h-[13px] w-[13px]" strokeWidth={2.4} />
                <span className="relative ml-px h-[11px] w-[22px] rounded-[3.5px] border-[1.5px] border-current opacity-90"><span className="absolute inset-[1.5px] right-[4px] rounded-[1.5px] bg-current" /><span className="absolute -right-[3px] top-[2.5px] h-[4px] w-[1.5px] rounded-r bg-current opacity-60" /></span>
              </span>
            </div>
            {/* home indicator */}
            <div className="absolute inset-x-0 bottom-0 z-40 flex items-center justify-center pointer-events-none" style={{ height: HOME_H }} aria-hidden><span className="h-[4px] w-[104px] rounded-full bg-ink/25" /></div>
            {/* glass: a faint diagonal reflection across the top-left of the display */}
            <span aria-hidden className="pointer-events-none absolute inset-0 z-20 rounded-[43px]" style={{ background: "linear-gradient(118deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 24%, rgba(255,255,255,0) 42%)" }} />
            {/* brand sweep — once per brand change */}
            {sweep > 0 && !reduced && <span key={sweep} aria-hidden className="brand-sweep pointer-events-none absolute inset-y-0 left-0 z-30 w-[70%]" style={{ background: "linear-gradient(90deg, transparent 0%, var(--demo-brand-soft) 35%, var(--demo-brand-glow) 50%, var(--demo-brand-soft) 65%, transparent 100%)" }} data-testid="hero-brand-sweep" />}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The tenant's mark: logo when we have one, else the initial on the brand colour. Crossfades on change. */
function TenantMark({ size = 22, radius = 7, className }) {
  const b = useDemoBrand();
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [b.logoUrl]);
  const initial = (b.name || "N").trim().charAt(0).toUpperCase();
  return (
    <span className={cn("relative inline-flex shrink-0 items-center justify-center overflow-hidden", className)} style={{ height: size, width: size, borderRadius: radius }} data-testid="demo-brand-mark">
      <AnimatePresence mode="popLayout" initial={false}>
        {b.logoUrl && !broken ? (
          <motion.img key={b.logoUrl} src={b.logoUrl} alt="" onError={() => setBroken(true)} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.35, ease: EASE }} className="h-full w-full object-contain" style={{ borderRadius: radius }} referrerPolicy="no-referrer" />
        ) : (
          <motion.span key={`i-${initial}`} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.35, ease: EASE }} className="flex h-full w-full items-center justify-center font-heading font-bold" style={{ background: BRAND, color: BRAND_FG, borderRadius: radius, fontSize: Math.round(size * 0.45) }}>
            {initial}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

/** The tenant's name, crossfading when the brand changes. */
function BrandName({ className }) {
  const b = useDemoBrand();
  return (
    <span className={cn("relative inline-grid", className)}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span key={b.name} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3, ease: EASE }} className="truncate">
          {b.name}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function TenantBar({ title, owner = false }) {
  const b = useDemoBrand();
  const shown = title || (owner ? b.ownerDomain : b.domain);
  return (
    <div className="h-11 shrink-0 flex items-center gap-2 px-[16px] border-b border-hairline" data-testid="phone-tenant-bar">
      <TenantMark />
      <BrandName className="font-heading font-bold text-[13.5px] tracking-[-0.02em] text-ink max-w-[120px]" />
      <span className="ml-auto inline-flex items-center gap-1 h-6 max-w-[160px] rounded-full bg-fill pl-2 pr-2.5 text-[10px] text-ink/55"><Lock className="h-2.5 w-2.5 shrink-0" strokeWidth={2.2} /><span className="truncate">{shown}</span></span>
    </div>
  );
}

function BrandButton({ children, className, pressed, muted, reduced, ...rest }) {
  return (
    <motion.span animate={pressed ? { scale: [1, 0.97, 1] } : { scale: 1 }} transition={{ duration: 0.4, ease: EASE }} className={cn("relative overflow-hidden h-11 rounded-full flex items-center justify-center gap-2 text-[13px] font-medium transition-opacity duration-500", muted ? "opacity-50" : "opacity-100", className)} style={{ background: BRAND, color: BRAND_FG }} {...rest}>
      {children}
      <TapRipple when={!!pressed} reduced={reduced} />
    </motion.span>
  );
}

/** Segmented choice — one option gets tapped. Options are strings or { label, icon }. */
function Choice({ options, pick, on, reduced, size = "md", testId }) {
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }} data-testid={testId} data-picked={on ? pick : ""}>
      {options.map((o) => {
        const label = typeof o === "string" ? o : o.label;
        const Icon = typeof o === "string" ? null : o.icon;
        const active = on && label === pick;
        return (
          <motion.span key={label} animate={active ? { scale: [1, 0.94, 1] } : { scale: 1 }} transition={{ duration: 0.35, ease: EASE }} className={cn("relative overflow-hidden rounded-[10px] border flex items-center justify-center gap-1.5 transition-colors duration-500", Icon ? "h-[46px] flex-col gap-1" : size === "md" ? "h-10 text-[13px]" : "h-9 text-[12px]", active ? "" : "border-hairline bg-surface text-ink/70")} style={active ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
            {Icon && <Icon className={cn("h-4 w-4", !active && "text-ink/55")} strokeWidth={1.8} />}
            <span className={cn(Icon && "text-[10.5px] font-medium leading-none")}>{label}</span>
            <TapRipple when={active} reduced={reduced} />
          </motion.span>
        );
      })}
    </div>
  );
}
const FieldLabel = ({ children, icon: Icon }) => (
  <p className="flex items-center gap-1.5 text-[11px] font-medium text-ink/60 mb-1.5">
    {Icon && <Icon className="h-3.5 w-3.5 text-ink/45" strokeWidth={1.9} />}
    {children}
  </p>
);

/* ---------- the map ----------
   An obviously illustrative city map (Lisbon's Baixa grid, a park, the river) drawn as SVG so it
   stays crisp at any scale. It is on screen from the moment Listed opens — the whole district,
   quietly dimmed, behind the address field. When the address is verified the camera zooms onto
   the street (transform only), the brand pin drops with a shadow, rings pulse once and the
   verified label lands. The pin lives outside the zooming layer so it keeps its size, like a real map. */
const MAP_W = 246;
const MAP_H = 200;
const PIN = { x: 130, y: 92 };
const ROADS_V = [10, 50, 90, 130, 170, 210];
const ROADS_H = [20, 60, 100, 140];
const BLOCKS = [];
[14, 54, 94, 134, 174, 214].forEach((x) => [24, 64, 104].forEach((y) => BLOCKS.push([x, y, 32, 32])));
[14, 54, 94, 174, 214].forEach((x) => BLOCKS.push([x, 144, 32, 22]));
const MAP_ZOOM = 1.75;
function MapScene({ reduced, active }) {
  const origin = `${(PIN.x / MAP_W) * 100}% ${(PIN.y / MAP_H) * 100}%`;
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#EEEBE4]" data-testid="hero-phone-map" data-active={active ? "true" : "false"}>
      <motion.div initial={false} animate={{ scale: active ? MAP_ZOOM : 1.04 }} transition={{ duration: reduced ? 0 : 1.7, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 will-change-transform" style={{ transformOrigin: origin }}>
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" aria-hidden>
          <defs>
            <linearGradient id="mapWater" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#C4DDEC" /><stop offset="1" stopColor="#B3D1E4" /></linearGradient>
          </defs>
          {/* the park, top left */}
          <path d="M -4 -4 L 46 -4 C 48 8, 40 16, 30 18 C 16 20, 6 14, -4 10 Z" fill="#D2E3C8" />
          {[[8, 4], [20, 8], [32, 4], [14, 12]].map(([cx, cy], k) => <circle key={`t${k}`} cx={cx} cy={cy} r="2.4" fill="#B9D1AC" />)}
          {/* blocks — a hairline of shadow below each, a courtyard in the square ones */}
          {BLOCKS.map(([x, y, w, h], k) => <rect key={`s${k}`} x={x + 0.6} y={y + 1.1} width={w} height={h} rx="2.4" fill="#D3CEC3" opacity="0.7" />)}
          {BLOCKS.map(([x, y, w, h], k) => <rect key={k} x={x} y={y} width={w} height={h} rx="2.4" fill={k % 3 === 0 ? "#E6E2DA" : "#E1DDD4"} />)}
          {BLOCKS.filter(([, , , h]) => h >= 30).map(([x, y, w, h], k) => <rect key={`c${k}`} x={x + w * 0.3} y={y + h * 0.3} width={w * 0.4} height={h * 0.4} rx="1.6" fill="#EEEBE4" />)}
          {/* the square by the river */}
          <rect x="134" y="144" width="32" height="24" rx="2" fill="#EAE6DD" />
          <circle cx="150" cy="156" r="3.2" fill="#DDD8CE" />
          {/* roads: casing then surface */}
          {ROADS_V.map((x) => <line key={`vc${x}`} x1={x} y1="-4" x2={x} y2="176" stroke="#D8D3C8" strokeWidth="6.5" />)}
          {ROADS_H.map((y) => <line key={`hc${y}`} x1="-4" y1={y} x2="250" y2={y} stroke="#D8D3C8" strokeWidth="6.5" />)}
          {ROADS_V.map((x) => <line key={`v${x}`} x1={x} y1="-4" x2={x} y2="176" stroke="#FFFFFF" strokeWidth="5" />)}
          {ROADS_H.map((y) => <line key={`h${y}`} x1="-4" y1={y} x2="250" y2={y} stroke="#FFFFFF" strokeWidth="5" />)}
          {/* the avenue — a touch wider, warmer */}
          <line x1="-4" y1="60" x2="250" y2="60" stroke="#FBF5D8" strokeWidth="6" />
          <line x1="90" y1="-4" x2="90" y2="176" stroke="#FBF5D8" strokeWidth="6" />
          {/* the street of the address — brand tinted */}
          <line x1="130" y1="-4" x2="130" y2="176" stroke="var(--demo-brand)" strokeWidth="5" opacity="0.3" />
          {/* the river */}
          <path d="M-4 178 C 50 172, 110 186, 170 180 S 230 172, 254 178 L 254 210 L -4 210 Z" fill="url(#mapWater)" />
          <path d="M-4 178 C 50 172, 110 186, 170 180 S 230 172, 254 178" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.8" />
          {/* labels */}
          <g fontFamily="var(--font-body)" fontSize="5.6" fill="#8E877B" fontWeight="500">
            <text x="133.5" y="38" transform="rotate(-90 133.5 38)" textAnchor="end">R. da Prata</text>
            <text x="93.5" y="38" transform="rotate(-90 93.5 38)" textAnchor="end">R. Augusta</text>
            <text x="53.5" y="38" transform="rotate(-90 53.5 38)" textAnchor="end">R. do Ouro</text>
            <text x="148" y="57.5">R. de Santa Justa</text>
            <text x="150" y="176" textAnchor="middle" fontSize="4.8">Praça do Comércio</text>
            <text x="22" y="86" fontSize="5">Chiado</text>
            <text x="222" y="196" fontSize="6" fill="#6F8FA6" fontStyle="italic">Tejo</text>
          </g>
        </svg>
      </motion.div>
      {/* the pin — a fixed point on screen while the map zooms beneath it */}
      <span className="absolute h-0 w-0" style={{ left: `${(PIN.x / MAP_W) * 100}%`, top: `${(PIN.y / MAP_H) * 100}%` }} aria-hidden data-testid="hero-phone-pin">
        {active && !reduced && (
          <>
            <span className="pin-pulse absolute -left-3 -top-3 h-6 w-6 rounded-full" style={{ background: BRAND, opacity: 0.5, animationDelay: "1.25s" }} />
            <span className="pin-pulse absolute -left-3 -top-3 h-6 w-6 rounded-full" style={{ background: BRAND, opacity: 0.5, animationDelay: "1.85s" }} />
          </>
        )}
        <motion.span initial={false} animate={active ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0.3 }} transition={{ delay: active ? 1.15 : 0, duration: 0.35 }} className="absolute -left-2 -top-[3px] h-[6px] w-4 rounded-[50%] bg-ink/30" style={{ filter: "blur(1.5px)" }} />
        <motion.span initial={false} animate={active ? { y: 0, opacity: 1, scale: 1 } : { y: -70, opacity: 0, scale: 0.6 }} transition={active ? { type: "spring", stiffness: 420, damping: 20, mass: 0.9, delay: 0.9 } : { duration: 0.2 }} className="absolute -left-[13px] -top-[34px] block h-[34px] w-[26px]" style={{ transformOrigin: "50% 100%", filter: "drop-shadow(0 8px 10px rgba(21,19,15,0.3))" }}>
          <svg viewBox="0 0 26 34" className="block h-full w-full">
            <path d="M13 0C5.8 0 0 5.8 0 13c0 9.1 13 21 13 21s13-11.9 13-21C26 5.8 20.2 0 13 0z" style={{ fill: "var(--demo-brand)" }} />
            <circle cx="13" cy="13" r="4.8" style={{ fill: "var(--demo-brand-fg)" }} />
          </svg>
        </motion.span>
      </span>
      {/* resting: the district waits, dimmed, until the address is verified */}
      <motion.span aria-hidden initial={false} animate={{ opacity: active ? 0 : 1 }} transition={{ duration: 0.9, ease: EASE }} className="pointer-events-none absolute inset-0 bg-[#F3F1EC]/60" />
      {/* the address callout floats up beside the pin once it has landed */}
      <motion.span initial={false} animate={active ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 6, scale: 0.94 }} transition={active ? { delay: 1.45, type: "spring", stiffness: 380, damping: 26 } : { duration: 0.2 }} className="absolute inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur pl-2 pr-2.5 h-[24px] text-[10.5px] font-medium text-ink shadow-[0_6px_18px_-8px_rgba(21,19,15,0.35)] whitespace-nowrap" style={{ left: `calc(${(PIN.x / MAP_W) * 100}% + 14px)`, top: `calc(${(PIN.y / MAP_H) * 100}% - 44px)` }} data-testid="hero-phone-map-label">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--demo-brand)" }} />
        {ADDRESS}
      </motion.span>
      {/* glass sheen */}
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 40%)" }} />
    </div>
  );
}

/** Numbers in `text` count up from 0 (ease-out), everything else stays put. */
function CountUp({ text, delay = 0.5, duration = 1100, reduced, className, ...rest }) {
  const parts = useMemo(() => String(text).split(/(\d[\d,]*)/), [text]);
  const [p, setP] = useState(reduced ? 1 : 0);
  useEffect(() => {
    if (reduced) {
      setP(1);
      return undefined;
    }
    let raf = 0;
    let t0 = 0;
    const id = setTimeout(() => {
      const step = (ts) => {
        if (!t0) t0 = ts;
        const k = Math.min(1, (ts - t0) / duration);
        setP(1 - (1 - k) ** 3);
        if (k < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay * 1000);
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, delay, duration, reduced]);
  return (
    <span className={className} {...rest}>
      {parts.map((seg, k) => {
        if (!/^\d[\d,]*$/.test(seg)) return <React.Fragment key={k}>{seg}</React.Fragment>;
        const n = Number(seg.replace(/,/g, ""));
        const v = Math.round(n * p);
        return <span key={k} className="tabular-nums">{seg.includes(",") ? v.toLocaleString("en-US") : String(v)}</span>;
      })}
    </span>
  );
}

/* ======================================================================
   Entry — the property manager's own website with the Listed entry point,
   and the estimate flow it expands into. One screen for two scenes:
     site    · a full-bleed hero, a glass nav, the page scrolls a little (it is
               a real site), then "Get my estimate" is tapped and the card
               grows into the page — the site recedes behind it.
     capture · inside the expanded card: address → Google Maps suggests →
               verified → the map flies in → continue.
   The browser bar stays put throughout: still {domain}, still their brand.
====================================================================== */
const SITE_TILES = [[LineChart, "l7.site.t1"], [HeartHandshake, "l7.site.t2"], [Wallet, "l7.site.t3"]];
/** The website's hero photograph — the visitor's own (read from their site) when we have it, else ours.
    Crossfades when the brand changes; falls back silently if their image cannot load. */
function SiteHeroPhoto({ reduced }) {
  const b = useDemoBrand();
  const [broken, setBroken] = useState("");
  const src = b.heroImageUrl && broken !== b.heroImageUrl ? b.heroImageUrl : SITE_IMG;
  return (
    <div className="absolute inset-0 bg-[#2B2925]" data-testid="hero-site-photo" data-own={src !== SITE_IMG ? "true" : "false"}>
      <AnimatePresence initial={false}>
        <motion.img key={src} src={src} alt="" decoding="async" onError={() => setBroken(src)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: EASE }} className={cn("absolute inset-0 h-full w-full object-cover", !reduced && "kenburns")} style={{ animationDuration: "22s" }} />
      </AnimatePresence>
    </div>
  );
}
const ENTRY = { heroH: 384, cardTop: 326, cardH: 136, inset: 14, scroll: 64, safari: 58 };
function ScreenEntry({ phase, reduced, live = true }) {
  const b = useDemoBrand();
  const capture = phase === "capture";
  // site phase (relative to the scene start)
  const scrollT = useAfter(1000, reduced, phase);
  const pressedT = useAfter(2450, reduced, phase);
  const expandT = useAfter(2750, reduced, phase);
  const scrolled = live && (scrollT || capture) && !reduced;
  const pressed = live && !capture && pressedT;
  const expanded = capture || (live && expandT);
  // capture phase (relative to the capture scene start)
  const { typed, done, count } = useTyped(ADDRESS, !live || !capture ? "idle" : reduced ? "done" : "run", 55, 700);
  const pickedT = useAfter(2400, reduced, phase);
  const contT = useAfter(4850, reduced, phase);
  const picked = capture && live && pickedT && done;
  const cont = capture && live && contT;
  const showSugg = capture && live && count >= 4 && !picked;
  const cardY = ENTRY.cardTop - (scrolled ? ENTRY.scroll : 0);
  const morph = { duration: 0.8, ease: IOS };
  // The page area the card grows into (design px — the stage is scaled as a whole, so this is stable).
  const area = useRef(null);
  const [areaH, setAreaH] = useState(552);
  useLayoutEffect(() => {
    if (area.current) setAreaH(area.current.clientHeight || 552);
  }, []);

  return (
    <div className="relative flex-1 min-h-0 bg-[#F7F5F0] overflow-hidden" data-testid="hero-phone-site-page" data-phase={phase} data-pressed={pressed ? "true" : "false"} data-scrolled={scrolled ? "true" : "false"} data-expanded={expanded ? "true" : "false"}>
      <div ref={area} className="absolute inset-x-0 top-0" style={{ bottom: ENTRY.safari }}>
        {/* the site — scrolls a little, then recedes behind the expanding card */}
        <motion.div animate={{ y: scrolled ? -ENTRY.scroll : 0, scale: expanded ? 0.94 : 1 }} transition={{ y: { duration: 1.1, ease: [0.22, 1, 0.36, 1] }, scale: morph }} className="absolute inset-x-0 top-0 will-change-transform" style={{ transformOrigin: "50% 30%" }}>
          <motion.div variants={item} className="relative overflow-hidden" style={{ height: ENTRY.heroH }}>
            <SiteHeroPhoto reduced={reduced} />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/5 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 px-[18px] pb-[74px] text-white">
              <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-white/75">{t("l7.site.eyebrow")}</p>
              <h4 className="mt-2 font-heading font-bold text-[27px] leading-[1.02] tracking-[-0.035em] text-balance drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">{t("l7.site.title")}</h4>
              <p className="mt-2 text-[12px] leading-[1.45] text-white/75 max-w-[24ch]">{t("l7.site.sub")}</p>
            </div>
          </motion.div>
          {/* room for the card */}
          <div style={{ height: ENTRY.cardH - (ENTRY.heroH - ENTRY.cardTop) }} aria-hidden />
          <motion.div variants={item} className="grid grid-cols-3 gap-2 px-[14px] pt-4">
            {SITE_TILES.map(([Icon, k]) => (
              <span key={k} className="flex flex-col items-start gap-2 rounded-[14px] bg-white p-2.5 shadow-hairSoft">
                <span className="flex h-7 w-7 items-center justify-center rounded-[9px]" style={{ background: BRAND_SOFT }}><Icon className="h-3.5 w-3.5" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
                <span className="text-[10.5px] font-medium text-ink leading-[1.2]">{t(k)}</span>
              </span>
            ))}
          </motion.div>
          <motion.div variants={item} className="px-[14px] pt-4 pb-6">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.16em] text-ink/45 font-medium">{t("l7.site.homes")}</p>
              <span className="inline-flex items-center gap-1 text-[10.5px] font-medium" style={{ color: BRAND_TEXT }}>{t("l7.site.all")} <ArrowRight className="h-3 w-3" strokeWidth={2.2} /></span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[THUMB, LIVE_IMG].map((src) => (
                <span key={src} className="relative h-[72px] overflow-hidden rounded-[12px]">
                  <img src={src} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* the site's nav — fixed, as on a real mobile site: the page scrolls beneath it, it leaves with the site */}
        <motion.div initial={false} animate={{ opacity: expanded ? 0 : 1, y: expanded ? -10 : 0 }} transition={{ duration: 0.4, ease: EASE }} className="absolute left-3 right-3 h-10 flex items-center justify-between px-3 rounded-[14px] bg-white/12 backdrop-blur-md border border-white/15 text-white will-change-transform" style={{ top: STATUS_H + 4 }} data-testid="hero-site-nav">
          <span className="inline-flex items-center gap-2 min-w-0"><TenantMark size={22} radius={6} /><BrandName className="font-heading font-bold text-[13.5px] tracking-[-0.02em] max-w-[120px]" /></span>
          <span className="flex flex-col gap-[3.5px] pr-0.5" aria-hidden><span className="h-[1.5px] w-4 bg-white rounded-full" /><span className="h-[1.5px] w-3 bg-white/80 rounded-full ml-auto" /></span>
        </motion.div>

        {/* scrim behind the expanded card */}
        <motion.span aria-hidden initial={false} animate={{ opacity: expanded ? 1 : 0 }} transition={morph} className="pointer-events-none absolute inset-0 bg-ink/35" />

        {/* the Listed entry point — a card on the site that grows into the page */}
        <motion.div
          initial={false}
          animate={expanded ? { top: 0, left: 0, right: 0, height: areaH, borderRadius: 0 } : { top: cardY, left: ENTRY.inset, right: ENTRY.inset, height: ENTRY.cardH, borderRadius: 18 }}
          transition={{ top: expanded ? morph : { duration: 1.1, ease: [0.22, 1, 0.36, 1] }, height: morph, left: morph, right: morph, borderRadius: morph }}
          className={cn("absolute overflow-hidden bg-white will-change-[top,height,left,right]", !expanded && "shadow-[0_24px_50px_-24px_rgba(21,19,15,0.45),0_0_0_1px_rgba(21,19,15,0.05)]")}
          data-testid="hero-site-entry"
          data-expanded={expanded ? "true" : "false"}
        >
          {/* the card, as it sits on the site */}
          <motion.div initial={false} animate={{ opacity: expanded ? 0 : 1, y: expanded ? -10 : 0 }} transition={{ duration: expanded ? 0.28 : 0.4, ease: EASE }} className="absolute inset-0 p-3.5 flex flex-col" style={{ pointerEvents: "none" }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l7.site.entry_eyebrow")}</p>
                <p className="mt-1 text-[13.5px] font-heading font-bold tracking-[-0.02em] text-ink leading-[1.15]">{t("l7.site.entry_title")}</p>
              </div>
              <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full bg-fill px-2 h-6 text-[10px] font-medium text-ink/70"><Clock className="h-3 w-3" strokeWidth={2.2} /> {t("l7.site.entry_time")}</span>
            </div>
            <BrandButton pressed={pressed} reduced={reduced} className="mt-auto h-11 text-[13px] shadow-[0_14px_28px_-14px_var(--demo-brand)]" data-testid="hero-site-cta">{t("l7.site.cta")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton>
          </motion.div>

          {/* the estimate flow, once the card is the page */}
          <motion.div initial={false} animate={{ opacity: expanded ? 1 : 0, y: expanded ? 0 : 14 }} transition={{ duration: 0.55, ease: EASE, delay: expanded ? 0.42 : 0 }} className="absolute inset-0 flex flex-col bg-canvas" style={{ pointerEvents: "none", paddingTop: STATUS_H }} data-testid="hero-phone-capture">
            <div className="h-11 shrink-0 flex items-center gap-2 px-[16px] border-b border-hairline" data-testid="phone-tenant-bar">
              <TenantMark />
              <BrandName className="font-heading font-bold text-[13.5px] tracking-[-0.02em] text-ink max-w-[150px]" />
              <span className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded-full bg-fill text-ink/60"><X className="h-3 w-3" strokeWidth={2.4} /></span>
            </div>
            <div className="px-[18px] pt-3.5 pb-3.5 flex-1 flex flex-col min-h-0">
              <p className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l7.ph.cap_eyebrow")}</p>
              <h4 className="mt-1.5 font-heading font-bold text-[24px] leading-[1.02] tracking-[-0.035em] text-ink text-balance">{t("l5.ph.site_title")}</h4>

              {/* the address field — suggestions float over the map beneath it */}
              <div className="relative z-20 mt-3.5">
                <div className={cn("relative rounded-[16px] border bg-surface transition-[border-color,box-shadow] duration-500", picked ? "border-hairline shadow-hairSoft" : showSugg || (capture && live && count > 0) ? "border-ink/35 shadow-[0_0_0_4px_rgba(21,19,15,0.04)]" : "border-hairline shadow-hairSoft")} data-testid="hero-phone-address">
                  <div className="flex items-center gap-2.5 h-[50px] px-3.5 text-[14px]">
                    <MapPin className="h-4 w-4 shrink-0" style={{ color: BRAND_TEXT }} strokeWidth={2} />
                    <span className="flex-1 min-w-0 truncate text-ink">
                      {picked ? `${ADDRESS}, ${CITY}` : typed || <span className="text-ink/35">{t("l5.ph.placeholder")}</span>}
                      {!picked && !reduced && capture && live && <span aria-hidden className="ml-px inline-block w-px h-4 bg-ink align-middle caret-blink" />}
                    </span>
                    <AnimatePresence>{picked && <motion.span key="ok" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 380, damping: 20 }}><Tick reduced={reduced} size={20} /></motion.span>}</AnimatePresence>
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {showSugg && (
                    <motion.div key="sugg" initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.22 } }} transition={{ duration: 0.32, ease: EASE }} className="absolute left-0 right-0 top-full mt-1.5 overflow-hidden rounded-[14px] border border-hairline bg-surface shadow-float will-change-transform" style={{ transformOrigin: "50% 0%" }} data-testid="hero-phone-suggestions">
                      {[[ADDRESS, true], ["Rua da Prata 44", false]].map(([sg, hi]) => (
                        <div key={sg} className={cn("relative flex items-center gap-2.5 px-3.5 py-2.5 transition-colors duration-500", hi && done ? "bg-fill" : "bg-surface")}>
                          <MapPin className="h-3.5 w-3.5 text-ink/40 shrink-0" />
                          <span className="min-w-0 flex-1"><span className="block text-[12.5px] text-ink">{sg}</span><span className="block text-[10.5px] text-ink/50">{t("l5.ph.sugg_meta")}</span></span>
                          {hi && <TapRipple when={done} reduced={reduced} dark />}
                        </div>
                      ))}
                      <div className="flex items-center justify-end gap-1.5 px-3.5 py-1.5 text-[9.5px] text-ink/40 border-t border-hairline"><BrandLogo id="googlemaps" scale={0.42} /></div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* the map — there from the start; zooms onto the street once the address is verified */}
              <div className="relative z-10 mt-3 flex-1 min-h-[120px] rounded-[16px] border border-hairline overflow-hidden" style={{ transform: "translateZ(0)" }}>
                <MapScene reduced={reduced} active={picked} />
              </div>

              <div className="pt-3 shrink-0">
                <BrandButton muted={!picked} pressed={cont} reduced={reduced}>{t("l7.ph.cta_continue")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton>
                <p className="mt-2.5 text-center text-[10px] text-ink/40">{t("brand.powered_by")}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Safari — the address bar lives at the bottom; it stays through the whole flow: still their domain */}
      <div className="absolute inset-x-0 bottom-0 z-30 border-t border-ink/[0.06] bg-[#F7F5F0]/85 backdrop-blur-xl" style={{ height: ENTRY.safari }} data-testid="phone-safari-bar">
        <div className="flex items-center gap-2 px-3.5 pt-2">
          <ChevronLeft className="h-4 w-4 text-ink/45" strokeWidth={2} />
          <ChevronRight className="h-4 w-4 text-ink/25" strokeWidth={2} />
          <span className="mx-auto flex h-[30px] w-full max-w-[164px] items-center justify-center gap-1.5 rounded-[10px] bg-white text-[11.5px] text-ink/75 shadow-hairSoft"><Lock className="h-3 w-3 text-ink/45" strokeWidth={2.2} /><span className="truncate">{b.domain}</span></span>
          <Share className="h-4 w-4 text-ink/45" strokeWidth={2} />
          <Copy className="h-4 w-4 text-ink/45" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

/* details · one question at a time: type → bedrooms + bathrooms → availability → note → estimate */
const DET_STEPS = ["type", "rooms", "from", "note"];
const stepSlide = {
  hidden: { opacity: 0, x: 56 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE, staggerChildren: 0.05, delayChildren: 0.08 } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.28, ease: [0.4, 0, 1, 1] } },
};
/** One tappable answer card. */
function OptionCard({ icon: Icon, label, hint, on, reduced, testId }) {
  return (
    <motion.div animate={on ? { scale: [1, 0.965, 1] } : { scale: 1 }} transition={{ duration: 0.4, ease: EASE }} className={cn("relative overflow-hidden flex items-center gap-3 h-[52px] rounded-[14px] border px-3.5 transition-colors duration-500", on ? "" : "border-hairline bg-surface")} style={on ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined} data-testid={testId} data-on={on ? "true" : "false"}>
      {Icon && <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] transition-colors duration-500", on ? "bg-white/20" : "bg-canvasAlt")}><Icon className="h-4 w-4" strokeWidth={1.9} style={on ? undefined : { color: BRAND_TEXT }} /></span>}
      <span className="min-w-0 flex-1">
        <span className={cn("block text-[13.5px] font-medium leading-tight truncate", !on && "text-ink")}>{label}</span>
        {hint && <span className={cn("block text-[10.5px] leading-tight mt-0.5 truncate", on ? "opacity-70" : "text-ink/50")}>{hint}</span>}
      </span>
      <AnimatePresence>{on && <motion.span key="ok" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 380, damping: 22 }} className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25"><Check className="h-3 w-3" strokeWidth={3} /></motion.span>}</AnimatePresence>
      <TapRipple when={on} reduced={reduced} />
    </motion.div>
  );
}
function ScreenDetails({ reduced }) {
  const s1 = useAfter(1400, reduced);
  const s2 = useAfter(3200, reduced);
  const s3 = useAfter(4100, reduced);
  const step = s3 ? 3 : s2 ? 2 : s1 ? 1 : 0;
  const typeOn = useAfter(900, reduced);
  const bedsOn = useAfter(2000, reduced);
  const bathsOn = useAfter(2600, reduced);
  const fromOn = useAfter(3650, reduced);
  const noteMode = useAfter(4400, reduced);
  const note = t("l7.ph.det_note_v");
  const { typed, done } = useTyped(note, reduced ? "done" : noteMode ? "run" : "idle", 22, 0);
  const pressed = useAfter(6100, reduced);
  const stepId = DET_STEPS[step];
  return (
    <>
      <TenantBar />
      <div className="px-[18px] pt-5 pb-4 flex-1 flex flex-col min-h-0" data-testid="hero-phone-details-form" data-step={stepId} data-filled={[typeOn, bedsOn && bathsOn, fromOn, done].filter(Boolean).length}>
        <motion.div variants={item} className="flex items-center gap-1.5" aria-hidden>
          {DET_STEPS.map((k, i) => (
            <span key={k} className="relative h-[3px] flex-1 rounded-full bg-hairline overflow-hidden">
              <motion.span animate={{ scaleX: i < step ? 1 : i === step ? 0.5 : 0 }} transition={{ duration: 0.5, ease: EASE }} className="absolute inset-0 origin-left rounded-full" style={{ background: BRAND }} />
            </span>
          ))}
        </motion.div>
        <motion.p variants={item} className="mt-4 text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l7.ph.det_eyebrow")}</motion.p>

        <div className="relative flex-1 min-h-0 mt-2">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div key={stepId} variants={stepSlide} initial="hidden" animate="show" exit="exit" className="absolute inset-0 flex flex-col will-change-transform" data-testid={`hero-det-step-${stepId}`}>
              {stepId === "type" && (
                <>
                  <motion.h4 variants={item} className="font-heading font-bold text-[24px] leading-[1.05] tracking-[-0.035em] text-ink text-balance">{t("l7.ph.det_q_type")}</motion.h4>
                  <motion.div variants={item} className="mt-5 space-y-2" data-testid="hero-det-type" data-picked={typeOn ? t("l7.ph.det_type_apartment") : ""}>
                    <OptionCard icon={Building2} label={t("l7.ph.det_type_apartment")} hint={t("l7.ph.det_type_apartment_h")} on={typeOn} reduced={reduced} />
                    <OptionCard icon={Home} label={t("l7.ph.det_type_house")} hint={t("l7.ph.det_type_house_h")} on={false} reduced={reduced} />
                    <OptionCard icon={TreePalm} label={t("l7.ph.det_type_villa")} hint={t("l7.ph.det_type_villa_h")} on={false} reduced={reduced} />
                  </motion.div>
                </>
              )}
              {stepId === "rooms" && (
                <>
                  <motion.h4 variants={item} className="font-heading font-bold text-[24px] leading-[1.05] tracking-[-0.035em] text-ink text-balance">{t("l7.ph.det_q_rooms")}</motion.h4>
                  <motion.div variants={item} className="mt-5">
                    <FieldLabel icon={BedDouble}>{t("l7.ph.det_beds")}</FieldLabel>
                    <Choice options={["1", "2", "3", "4+"]} pick="3" on={bedsOn} reduced={reduced} testId="hero-det-beds" />
                  </motion.div>
                  <motion.div variants={item} className="mt-4">
                    <FieldLabel icon={Bath}>{t("l7.ph.det_baths")}</FieldLabel>
                    <Choice options={["1", "2", "3+"]} pick="2" on={bathsOn} reduced={reduced} testId="hero-det-baths" />
                  </motion.div>
                </>
              )}
              {stepId === "from" && (
                <>
                  <motion.h4 variants={item} className="font-heading font-bold text-[24px] leading-[1.05] tracking-[-0.035em] text-ink text-balance">{t("l7.ph.det_q_from")}</motion.h4>
                  <motion.div variants={item} className="mt-5 space-y-2" data-testid="hero-det-from" data-picked={fromOn ? t("l7.ph.det_from_june") : ""}>
                    <OptionCard icon={Zap} label={t("l7.ph.det_from_now")} hint={t("l7.ph.det_from_now_h")} on={false} reduced={reduced} />
                    <OptionCard icon={Sun} label={t("l7.ph.det_from_june")} hint={t("l7.ph.det_from_june_h")} on={fromOn} reduced={reduced} />
                    <OptionCard icon={CalendarClock} label={t("l7.ph.det_from_later")} hint={t("l7.ph.det_from_later_h")} on={false} reduced={reduced} />
                  </motion.div>
                </>
              )}
              {stepId === "note" && (
                <>
                  <motion.h4 variants={item} className="font-heading font-bold text-[24px] leading-[1.05] tracking-[-0.035em] text-ink text-balance">{t("l7.ph.det_note")}</motion.h4>
                  <motion.div variants={item} className={cn("mt-5 rounded-[14px] border bg-surface px-3.5 py-3 min-h-[92px] text-[13px] leading-[1.5] text-ink transition-[border-color,box-shadow] duration-500", noteMode && !done ? "border-ink/35 shadow-[0_0_0_4px_rgba(21,19,15,0.04)]" : "border-hairline")} data-testid="hero-det-note">
                    {typed || (!noteMode && <span className="text-ink/35">{t("l7.ph.det_note_ph")}</span>)}
                    {noteMode && !done && !reduced && <span aria-hidden className="ml-px inline-block w-px h-3.5 bg-ink align-middle caret-blink" />}
                  </motion.div>
                  <motion.p variants={item} className="mt-2 text-[10.5px] text-ink/45">{t("l7.ph.det_note_hint")}</motion.p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* answers so far — every tap becomes a structured field */}
        <div className="mt-3 flex flex-wrap gap-1.5 min-h-[26px]" data-testid="hero-det-answers" aria-hidden>
          <AnimatePresence initial={false}>
            {[[typeOn, Building2, t("l7.ph.det_type_apartment")], [bedsOn, BedDouble, t("l7.ph.est_beds")], [bathsOn, Bath, t("l7.ph.est_baths")], [fromOn, CalendarDays, t("l7.ph.det_from_june")]].filter(([on]) => on).map(([, Icon, label]) => (
              <motion.span key={label} layout initial={{ opacity: 0, scale: 0.8, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 360, damping: 24 }} className="inline-flex items-center gap-1.5 h-[26px] rounded-full border border-hairline bg-surface pl-2 pr-2.5 text-[11px] font-medium text-ink">
                <Icon className="h-3 w-3 shrink-0" style={{ color: BRAND_TEXT }} strokeWidth={2} />{label}
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
        <div className="mt-auto pt-3 min-h-[80px]">
          <AnimatePresence mode="wait" initial={false}>
            {stepId === "note" ? (
              <motion.div key="cta" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}>
                <BrandButton muted={!done} pressed={pressed} reduced={reduced}>{t("l7.ph.det_cta")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton>
                <p className="mt-2.5 text-center text-[10px] text-ink/40">{t("l7.ph.det_privacy")}</p>
              </motion.div>
            ) : (
              <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-[10.5px] text-ink/40 h-11 flex items-center justify-center">{t("l7.ph.det_tap_hint")}</motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

/* estimate — her answers are now structured fields; the number lands */
const SEASON = [0.42, 0.46, 0.58, 0.74, 0.86, 1, 1, 1, 0.9, 0.7, 0.5, 0.56]; // Lisbon: summer peaks, quieter winter
const EST_FACTS = [[Building2, "l7.ph.det_type_apartment"], [BedDouble, "l7.ph.est_beds"], [Bath, "l7.ph.est_baths"], [CalendarDays, "l7.ph.det_from_june"]];
function ScreenEstimate({ reduced }) {
  const sent = useAfter(1500, reduced);
  const next = useAfter(2400, reduced);
  return (
    <>
      <TenantBar />
      <div className="px-[18px] pt-4 pb-4 flex-1 flex flex-col min-h-0">
        <motion.p variants={item} className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l5.ph.est_eyebrow")}</motion.p>
        <motion.div variants={item} className="mt-3 flex items-center gap-3">
          <img src={THUMB} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-[10px] object-cover" />
          <div className="min-w-0"><p className="font-heading font-bold text-[17px] tracking-[-0.03em] text-ink leading-none truncate">{ADDRESS}</p><p className="mt-1 text-[11px] text-ink/55 truncate">{t("l5.ph.est_meta")}</p></div>
        </motion.div>
        <motion.div variants={item} className="mt-3 flex flex-wrap gap-1.5" data-testid="hero-phone-estimate-facts">
          {EST_FACTS.map(([Icon, k]) => (
            <span key={k} className="inline-flex items-center gap-1.5 h-7 rounded-full border border-hairline bg-surface pl-2 pr-2.5 text-[11px] font-medium text-ink">
              <Icon className="h-3 w-3 shrink-0" style={{ color: BRAND_TEXT }} strokeWidth={1.9} />
              {t(k)}
            </span>
          ))}
        </motion.div>
        <motion.div variants={item} className="relative mt-3 rounded-[16px] text-white p-4 overflow-hidden" style={{ background: BRAND_DEEP }} data-testid="hero-phone-estimate">
          <span aria-hidden className="pointer-events-none absolute -right-10 -top-14 h-36 w-36 rounded-full opacity-70" style={{ background: "radial-gradient(closest-side, var(--demo-brand-glow), transparent 72%)" }} />
          <p className="relative text-[9.5px] uppercase tracking-[0.18em]" style={{ color: BRAND_LIGHT }}>{t("l5.ph.est_label")}</p>
          <motion.p initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }} className="relative mt-1.5 font-heading font-bold text-[26px] leading-tight tracking-[-0.035em] tabular-nums"><CountUp text={t("l5.ph.est_value")} delay={0.5} reduced={reduced} data-testid="hero-phone-estimate-value" /></motion.p>
          {/* twelve months — the shape of the year grows in under the number */}
          <div className="relative mt-3 flex items-end gap-[3px] h-[34px]" aria-hidden data-testid="hero-phone-estimate-season">
            {SEASON.map((v, i) => (
              <motion.span key={i} initial={reduced ? false : { scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: 0.9 + i * 0.045, duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="flex-1 rounded-[2px] origin-bottom" style={{ height: `${Math.round(v * 100)}%`, background: v >= 0.95 ? BRAND_LIGHT : "rgba(255,255,255,0.28)" }} />
            ))}
          </div>
          <div className="relative mt-1 flex justify-between text-[8.5px] uppercase tracking-[0.1em] text-white/40"><span>Jan</span><span>Jul</span><span>Dec</span></div>
          <p className="relative mt-2 text-[11px] text-white/60 leading-[1.45]">{t("l5.ph.est_note")}</p>
        </motion.div>
        <div className="mt-3 space-y-2">
          <Appear when={sent} className="flex items-center gap-2.5 rounded-[12px] border border-hairline bg-surface px-3 h-10 text-[12px] text-ink"><Tick reduced={reduced} size={16} /> <span className="truncate">{t("l5.ph.est_sent")}</span></Appear>
          <Appear when={next} className="rounded-[12px] border border-hairline bg-surface px-3 py-3 text-[12px] text-ink" data-testid="hero-phone-next-steps">
            <p className="text-[10px] uppercase tracking-[0.14em] text-ink/45">{t("l5.ph.est_next_title")}</p>
            <ol className="mt-2 space-y-2">
              {["n1", "n2", "n3"].map((k, i) => (
                <li key={k} className="flex items-start gap-2.5 leading-[1.4]"><span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[9.5px] font-bold" style={{ background: BRAND, color: BRAND_FG }}>{i + 1}</span><span className="text-ink/85">{t(`l5.ph.est_${k}`)}</span></li>
              ))}
            </ol>
          </Appear>
        </div>
        <motion.p variants={item} className="mt-auto text-center text-[10px] text-ink/40">{t("brand.powered_by")}</motion.p>
      </div>
    </>
  );
}

/* offer · lock-screen notification → she opens the branded offer page → adds photography → accepts */
function ScreenOffer({ reduced }) {
  const opened = useAfter(1700, reduced);
  return (
    <AnimatePresence mode="wait" initial={false}>
      {!opened ? (
        <motion.div key="lock" exit={{ opacity: 0, scale: 1.02, transition: { duration: 0.35, ease: [0.4, 0, 1, 1] } }} className="absolute inset-0 flex flex-col">
          <LockScreen reduced={reduced} />
        </motion.div>
      ) : (
        <motion.div key="page" initial={reduced ? false : { opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, ease: EASE }} className="absolute inset-x-0 flex flex-col" style={{ top: STATUS_H, bottom: HOME_H }}>
          <OfferPage reduced={reduced} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
function LockScreen({ reduced }) {
  const arrived = useAfter(700, reduced, "", true);
  return (
    <div className="flex-1 flex flex-col relative">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at top, var(--demo-brand-soft), transparent 60%)" }} aria-hidden />
      <div className="relative p-[18px] flex-1 flex flex-col">
        <motion.p variants={item} className="text-center font-heading text-[54px] font-bold tracking-[-0.04em] text-ink leading-none mt-4 tabular-nums">09:16</motion.p>
        <motion.p variants={item} className="text-center text-[12px] text-ink/55 mt-1">Tuesday, 3 June</motion.p>
        <AnimatePresence>
          {arrived && (
            <motion.div key="n" initial={reduced ? false : { opacity: 0, y: -18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 26 }} className="mt-8 rounded-[18px] bg-surface/95 border border-hairline p-3.5 shadow-float" data-testid="hero-phone-notification">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-[10px]" style={{ background: BRAND, color: BRAND_FG }}><Mail className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2"><p className="text-[12.5px] font-medium text-ink truncate"><BrandName /></p><span className="text-[10px] text-ink/40">now</span></div>
                  <p className="text-[12px] text-ink truncate">{t("l5.ph.notif")}</p>
                  <p className="text-[11px] text-ink/55 truncate">{t("l5.ph.notif_sub")}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <p className="mt-auto text-center text-[10.5px] text-ink/40">{t("l6.h.lock.hint")}</p>
      </div>
    </div>
  );
}
function OfferPage({ reduced }) {
  const scroller = useRef(null);
  const content = useRef(null);
  const extras = useRef(null);
  const [y1, setY1] = useState(-160);
  const [y2, setY2] = useState(-360);
  // OfferPage mounts ~1700ms into the scene (after the lock screen); timings are relative to mount.
  const phase1 = useAfter(1600, reduced);
  const added = useAfter(2800, reduced);
  const phase2 = useAfter(3800, reduced);
  const pressed = useAfter(5000, reduced);
  const accepted = useAfter(5400, reduced);
  useLayoutEffect(() => {
    const sc = scroller.current;
    const ct = content.current;
    const ex = extras.current;
    if (!sc || !ct || !ex) return;
    // Never scroll past the end of the page — a real page can't either.
    const max = Math.max(0, ct.offsetHeight - sc.clientHeight);
    setY1(-Math.min(Math.max(0, ex.offsetTop - 14), max));
    setY2(-max);
  }, [added, accepted]);
  const y = reduced ? 0 : phase2 ? y2 : phase1 ? y1 : 0;
  return (
    <>
      <TenantBar owner />
      <div ref={scroller} className="flex-1 min-h-0 overflow-hidden relative" data-testid="hero-phone-offer-scroller">
        <motion.div ref={content} animate={{ y }} transition={{ duration: 1.0, ease: EASE }} className="relative px-[18px] pt-4 pb-4">
          <motion.div variants={screen} initial="hidden" animate="show">
            <motion.p variants={item} className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l5.ph.offer_eyebrow")}</motion.p>
            <motion.h4 variants={item} className="mt-1.5 font-heading font-bold text-[19px] leading-[1.1] tracking-[-0.03em] text-ink text-balance">{t("l5.ph.offer_title")}</motion.h4>
            <motion.p variants={item} className="mt-1.5 text-[10.5px] text-ink/50">{t("l5.ph.offer_meta")}</motion.p>

            <motion.div variants={item} className="relative mt-4 rounded-[14px] text-white p-3.5 overflow-hidden" style={{ background: BRAND_DEEP }}>
              <span aria-hidden className="pointer-events-none absolute -right-10 -top-14 h-32 w-32 rounded-full opacity-70" style={{ background: "radial-gradient(closest-side, var(--demo-brand-glow), transparent 72%)" }} />
              <p className="relative text-[9.5px] uppercase tracking-[0.18em]" style={{ color: BRAND_LIGHT }}>{t("l5.ph.est_label")}</p>
              <p className="mt-1.5 font-heading font-bold text-[17px] leading-tight tracking-[-0.03em] tabular-nums">{t("l5.ph.est_value")}</p>
              <p className="mt-1.5 text-[10.5px] text-white/60">{t("l5.ph.est_note")}</p>
            </motion.div>

            <motion.div variants={item} className="mt-3 rounded-[14px] border border-hairline bg-surface p-3.5">
              <p className="text-[9.5px] uppercase tracking-[0.18em] text-ink/45">{t("l5.ph.offer_plan")}</p>
              <div className="mt-1.5 flex items-start justify-between gap-3">
                <p className="font-heading font-bold text-[16px] text-ink tracking-[-0.02em]">{t("l5.ph.offer_plan_name")}</p>
                <div className="text-right shrink-0"><p className="font-heading font-bold text-[20px] text-ink leading-none">{t("l5.ph.offer_plan_pct")}</p><p className="text-[9.5px] text-ink/50 mt-0.5">{t("l5.ph.offer_of_revenue")}</p></div>
              </div>
              <ul className="mt-3 pt-3 border-t border-hairline space-y-1.5">
                {["inc1", "inc2", "inc3"].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-[11.5px] text-ink"><Check className="h-3 w-3 text-success shrink-0" strokeWidth={3} /><span className="truncate">{t(`l5.ph.offer_${k}`)}</span></li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          <div ref={extras} className="mt-5">
            <p className="font-heading font-bold text-[14px] text-ink tracking-[-0.02em]">{t("l5.ph.offer_extras")}</p>
            <p className="text-[10.5px] text-ink/50 mt-0.5">{t("l5.ph.offer_extras_sub")}</p>
            <div className="mt-2.5 space-y-2">
              <OfferExtra name={t("l5.ph.offer_photo")} price={t("l5.ph.offer_photo_p")} on={added} recommended reduced={reduced} testId="hero-offer-extra-photo" />
              <OfferExtra name={t("l5.ph.offer_styling")} price={t("l5.ph.offer_styling_p")} on={false} reduced={reduced} />
            </div>
          </div>

          <div className="mt-4 rounded-[14px] bg-canvasAlt p-3.5">
            <p className="font-heading font-bold text-[13px] text-ink tracking-[-0.02em]">{t("l5.ph.offer_summary")}</p>
            <dl className="mt-2 space-y-1.5 text-[11.5px]">
              <div className="flex items-center justify-between gap-3"><dt className="text-ink/55">{t("l5.ph.offer_fee")}</dt><dd className="text-ink font-medium">15%</dd></div>
              <div className="flex items-center justify-between gap-3 h-4"><dt className="text-ink/55">{t("l5.ph.offer_one_time")}</dt>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.dd key={added ? "a" : "b"} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.25 }} className="text-ink font-medium tabular-nums">{added ? "€490" : "€0"}</motion.dd>
                </AnimatePresence>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-hairline"><dt className="text-ink/55">{t("l5.ph.offer_net_short")}</dt>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.dd key={added ? "a" : "b"} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.25 }} className="font-heading font-bold text-[14px] text-ink tabular-nums whitespace-nowrap">{added ? t("l5.ph.offer_net_v2") : t("l5.ph.offer_net_v")}</motion.dd>
                </AnimatePresence>
              </div>
            </dl>
          </div>

          <div className="mt-4" data-testid="hero-offer-accept">
            <AnimatePresence mode="wait" initial={false}>
              {accepted ? (
                <motion.div key="ok" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }} className="h-11 rounded-full bg-successBg text-success flex items-center justify-center gap-2 text-[13px] font-medium"><Check className="h-4 w-4" strokeWidth={3} /> {t("l5.ph.offer_accepted")}</motion.div>
              ) : (
                <motion.div key="cta" exit={{ opacity: 0 }}><BrandButton pressed={pressed} reduced={reduced}>{t("l5.ph.offer_accept")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton></motion.div>
              )}
            </AnimatePresence>
            <p className="mt-2.5 text-center text-[10px] text-ink/40">{t("brand.powered_by")}</p>
          </div>
        </motion.div>
      </div>
    </>
  );
}

function OfferExtra({ name, price, on, recommended, reduced, testId }) {
  return (
    <div className={cn("rounded-[12px] border bg-surface px-3 py-2.5 flex items-center gap-3 transition-colors duration-500", on ? "" : "border-hairline")} style={on ? { borderColor: BRAND } : undefined} data-testid={testId} data-on={on ? "true" : "false"}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap"><p className="text-[12.5px] font-medium text-ink leading-tight">{name}</p>{recommended && <span className="inline-flex rounded-full px-1.5 py-px text-[9px] font-medium bg-[rgba(212,150,255,0.22)] text-ink">{t("chip.recommended")}</span>}</div>
        <p className="text-[11px] text-ink/55 mt-0.5">{price}</p>
      </div>
      <motion.span animate={on ? { scale: [1, 0.94, 1] } : { scale: 1 }} transition={{ duration: 0.35, ease: EASE }} className={cn("relative overflow-hidden inline-flex items-center justify-center gap-1 rounded-full border px-2.5 h-7 min-w-[74px] text-[11px] font-medium shrink-0 transition-colors duration-500", on ? "" : "border-hairline text-ink")} style={on ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
        {on ? <><Check className="h-3 w-3" strokeWidth={3} /> {t("chip.added")}</> : <><Plus className="h-3 w-3" /> {t("chip.add")}</>}
        <TapRipple when={on} reduced={reduced} />
      </motion.span>
    </div>
  );
}

/* sign */
function ScreenSign({ reduced }) {
  const pad = useAfter(600, reduced);
  const signed = useAfter(2600, reduced);
  return (
    <>
      <TenantBar owner />
      <div className="px-[18px] pt-4 pb-4 flex-1 flex flex-col min-h-0">
        <motion.p variants={item} className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l5.ph.sign_eyebrow")}</motion.p>
        <motion.h4 variants={item} className="mt-1.5 font-heading font-bold text-[19px] tracking-[-0.03em] text-ink leading-tight">{t("l5.ph.sign_title")}</motion.h4>
        <motion.p variants={item} className="mt-1 text-[11px] text-ink/50">{t("l5.ph.sign_meta")}</motion.p>
        <motion.ul variants={item} className="mt-4 rounded-[14px] border border-hairline bg-surface divide-y divide-hairline text-[12.5px]">
          {[[t("l5.ph.offer_plan_name"), "15%"], [t("l5.ph.offer_photo"), "€490"]].map(([n, p]) => (
            <li key={n} className="flex items-center justify-between gap-2 px-3.5 h-10">
              <span className="inline-flex items-center gap-2 text-ink truncate"><Check className="h-3 w-3 text-success shrink-0" strokeWidth={3} />{n}</span>
              <span className="text-ink shrink-0">{p}</span>
            </li>
          ))}
          {[[t("l5.d.sign.term"), t("l6.h.sign.term_short")], [t("l5.d.sign.start"), t("l5.d.sign.start_v")]].map(([n, p]) => (
            <li key={n} className="flex items-center justify-between gap-2 px-3.5 h-9 text-[11.5px]">
              <span className="text-ink/55 truncate">{n}</span>
              <span className="text-ink shrink-0">{p}</span>
            </li>
          ))}
        </motion.ul>
        <Appear when={pad} y={8} className="mt-4">
          <div className="relative h-[96px] rounded-[14px] border border-dashed border-ink/25 bg-surface overflow-hidden" data-testid="hero-phone-signature">
            <motion.span initial={{ opacity: 1 }} animate={{ opacity: signed ? 0 : 0.5 }} className="absolute left-3.5 top-2.5 text-[10px] uppercase tracking-[0.16em] text-ink">{t("l5.ph.sign_here")}</motion.span>
            <svg viewBox="0 0 256 76" className="absolute inset-0 h-full w-full" aria-hidden>
              <motion.path d={SIG_D} fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" initial={reduced ? false : { pathLength: 0, opacity: 0.5 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.5, ease: [0.45, 0, 0.2, 1], delay: 0.35 }} />
            </svg>
            <span aria-hidden className="absolute left-3.5 right-3.5 bottom-3 h-px bg-ink/15" />
          </div>
        </Appear>
        <div className="mt-auto pt-4 min-h-[96px]">
          <AnimatePresence mode="wait" initial={false}>
            {signed ? (
              <motion.div key="ok" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
                <div className="flex items-center gap-2.5 rounded-[12px] bg-successBg px-3 h-10 text-[12.5px] font-medium text-success"><Tick reduced={reduced} size={16} /> {t("l5.ph.signed")}<span className="ml-auto font-normal text-[10.5px] text-success/80 truncate">{t("l6.h.sign.copy")}</span></div>
                <BrandButton className="mt-2.5 h-10 text-[12.5px]">{t("l6.h.sign.next")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton>
              </motion.div>
            ) : (
              <motion.p key="hint" exit={{ opacity: 0 }} className="text-center text-[11.5px] text-ink/50 h-11 flex items-center justify-center">{pad ? t("l5.ph.signing") : ""}</motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

/* photo · she books the shoot: day → time → confirm */
const WEEK = [["Mon", "9"], ["Tue", "10"], ["Wed", "11"], ["Thu", "12"], ["Fri", "13"], ["Sat", "14"], ["Sun", "15"]];
const FREE = new Set(["10", "12", "13"]);
const SLOTS = ["09:00", "10:00", "13:00", "15:00"];
function ScreenPhoto({ reduced }) {
  const dayOn = useAfter(1300, reduced);
  const slotOn = useAfter(2300, reduced);
  const pressed = useAfter(3300, reduced);
  const booked = useAfter(3700, reduced);
  return (
    <>
      <TenantBar owner />
      <div className="px-[18px] pt-4 pb-4 flex-1 flex flex-col min-h-0" data-testid="hero-phone-photo-form" data-booked={booked ? "true" : "false"}>
        <motion.p variants={item} className="text-[10.5px] uppercase tracking-[0.16em] font-medium" style={{ color: BRAND_TEXT }}>{t("l7.ph.photo_eyebrow")}</motion.p>
        <motion.h4 variants={item} className="mt-1.5 font-heading font-bold text-[20px] leading-[1.1] tracking-[-0.03em] text-ink text-balance">{t("l7.ph.photo_title")}</motion.h4>
        <motion.p variants={item} className="mt-1.5 text-[12px] text-ink/60 leading-[1.45]">{t("l7.ph.photo_sub")}</motion.p>

        <motion.div variants={item} className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <FieldLabel>{t("l7.ph.photo_week")}</FieldLabel>
            <span className="inline-flex items-center gap-1 text-[10.5px] text-ink/45"><span className="h-1.5 w-1.5 rounded-full" style={{ background: BRAND }} /> {t("l7.ph.photo_free")}</span>
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="hero-photo-days" data-picked={dayOn ? "12" : ""}>
            {WEEK.map(([d, n]) => {
              const active = dayOn && n === "12";
              const free = FREE.has(n);
              return (
                <motion.span key={n} animate={active ? { scale: [1, 0.94, 1] } : { scale: 1 }} transition={{ duration: 0.35, ease: EASE }} className={cn("relative overflow-hidden h-[58px] rounded-[10px] border flex flex-col items-center justify-center transition-colors duration-500", active ? "" : free ? "border-hairline bg-surface text-ink" : "border-transparent bg-transparent text-ink/30")} style={active ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
                  <span className="text-[9px] uppercase tracking-[0.08em] opacity-70">{d}</span>
                  <span className="mt-0.5 text-[13px] font-medium tabular-nums">{n}</span>
                  {free && !active && <span aria-hidden className="absolute bottom-1.5 h-1 w-1 rounded-full" style={{ background: BRAND }} />}
                  <TapRipple when={active} reduced={reduced} />
                </motion.span>
              );
            })}
          </div>
        </motion.div>

        <motion.div variants={item} className="mt-3">
          <FieldLabel>{t("l7.ph.photo_slots")}</FieldLabel>
          <Choice options={SLOTS} pick="10:00" on={slotOn} reduced={reduced} size="sm" testId="hero-photo-slots" />
        </motion.div>

        <motion.div variants={item} className="mt-4 rounded-[12px] border border-hairline bg-surface divide-y divide-hairline">
          {[[Camera, t("l7.ph.photo_meta"), t("l7.ph.photo_meta_sub")], [MapPin, `${ADDRESS}, ${CITY}`, t("l7.ph.photo_onsite")]].map(([Icon, a, b]) => (
            <div key={a} className="flex items-center gap-3 px-3 h-12">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-canvasAlt"><Icon className="h-3.5 w-3.5" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-ink truncate">{a}</p>
                <p className="text-[10.5px] text-ink/50 truncate">{b}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="mt-auto pt-3 min-h-[92px]">
          <AnimatePresence mode="wait" initial={false}>
            {booked ? (
              <motion.div key="ok" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }} data-testid="hero-photo-booked">
                <div className="flex items-center gap-2.5 rounded-[12px] bg-successBg px-3 h-11 text-[12.5px] font-medium text-success"><Tick reduced={reduced} size={16} /> <span className="truncate">{t("l7.ph.photo_done")}</span></div>
                <p className="mt-2 text-center text-[10.5px] text-ink/50 leading-[1.4]">{t("l7.ph.photo_done_sub")}</p>
              </motion.div>
            ) : (
              <motion.div key="cta" exit={{ opacity: 0 }}>
                <BrandButton muted={!slotOn} pressed={pressed} reduced={reduced}><CalendarDays className="h-3.5 w-3.5" /> {t("l7.ph.photo_cta")}</BrandButton>
                <p className="mt-2 text-center text-[10px] text-ink/40 inline-flex w-full items-center justify-center gap-1"><Clock className="h-3 w-3" /> {t("l7.ph.photo_hint")}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

/* ======================================================================
   Onboarding — the owner gets the home ready on the phone, one focused
   step at a time. A ring fills as each answer lands on the record:
   Wi-Fi read from a router photo (AI) → house rules → the shoot's photos
   arrive by themselves → first availability → 100% ready, and Listed
   hands the property to the PMS (a quiet banner, like a system notice).
====================================================================== */
const ONB_STEPS = [
  ["access", KeyRound],
  ["wifi", Wifi],
  ["rules", ScrollText],
  ["photos", ImageIcon],
  ["calendar", CalendarDays],
  ["ready", Radio],
];
const RULES = ["No parties", "Quiet 22–07", "No smoking", "Pets on request"];
const ONB_PHOTOS = ["/img/interior-living.webp", "/img/interior-kitchen.webp", "/img/interior-bedroom.webp", "/img/interior-dining.webp", "/img/interior-hallway.webp", "/img/interior-openplan.webp"];
const ONB_DAYS = [["Mon", "18"], ["Tue", "19"], ["Wed", "20"], ["Thu", "21"], ["Fri", "22"], ["Sat", "23"], ["Sun", "24"]];
const onbCard = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: IOS, staggerChildren: 0.06, delayChildren: 0.12 } },
  exit: { opacity: 0, x: -28, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } },
};

function Ring({ value, size = 46, stroke = 4 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }} data-testid="hero-onb-ring" data-value={Math.round(value * 100)}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgb(var(--hairline-rgb))" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={BRAND} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} animate={{ strokeDashoffset: c * (1 - value) }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading font-bold text-[11px] tabular-nums text-ink"><RingPercent value={value} /></span>
    </span>
  );
}
/** 0..1 → "62%" that counts toward its target. */
function RingPercent({ value }) {
  const [v, setV] = useState(value);
  useEffect(() => {
    let raf = 0;
    const from = v;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / 800);
      const e = 1 - Math.pow(1 - p, 3);
      setV(from + (value - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{Math.round(v * 100)}%</>;
}

function ScreenOnboard({ reduced }) {
  // one clock, one storyline — every moment below is ms since the scene began
  const shutter = useAfter(900, reduced);
  const photo = useAfter(1150, reduced);
  const read = useAfter(2100, reduced);
  const confirm = useAfter(2600, reduced);
  const wifiDone = useAfter(2900, reduced);
  const r1 = useAfter(3200, reduced);
  const r2 = useAfter(3450, reduced);
  const r3 = useAfter(3700, reduced);
  const rulesTap = useAfter(4100, reduced);
  const rulesDone = useAfter(4300, reduced);
  const photosIn = useAfter(4500, reduced);
  const photosDone = useAfter(5600, reduced);
  const dayPick = useAfter(6100, reduced);
  const calDone = useAfter(6700, reduced);
  const handoff = useAfter(7500, reduced);
  const step = calDone ? "ready" : photosDone ? "calendar" : rulesDone ? "photos" : wifiDone ? "rules" : "wifi";
  const doneCount = 1 + (wifiDone ? 1 : 0) + (rulesDone ? 1 : 0) + (photosDone ? 1 : 0) + (calDone ? 1 : 0) + (calDone ? 1 : 0);
  const value = doneCount / ONB_STEPS.length;
  const b = useDemoBrand();
  return (
    <>
      <TenantBar owner />
      <div className="relative px-[18px] pt-3.5 pb-4 flex-1 flex flex-col min-h-0" data-testid="hero-phone-onboarding" data-step={step} data-progress={Math.round(value * 100)}>
        {/* header: what we are doing + how far along */}
        <motion.div variants={item} className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.16em] font-medium truncate" style={{ color: BRAND_TEXT }}>{t("l5.ph.onb_eyebrow")}</p>
            <h4 className="mt-1 font-heading font-bold text-[19px] tracking-[-0.03em] text-ink leading-[1.08] text-balance">{ADDRESS}</h4>
            <AnimatePresence mode="wait" initial={false}>
              <motion.p key={step === "ready" ? "d" : "s"} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -3 }} transition={{ duration: 0.3 }} className={cn("mt-1 text-[11.5px] leading-snug", step === "ready" ? "text-success font-medium" : "text-ink/55")}>
                {step === "ready" ? t("l5.ph.onb_done") : t("l5.ph.onb_sub")}
              </motion.p>
            </AnimatePresence>
          </div>
          <Ring value={value} />
        </motion.div>

        {/* the six steps — done, current, next */}
        <motion.ol variants={item} className="mt-3.5 grid grid-cols-6 gap-1.5" aria-hidden data-testid="hero-onb-steps">
          {ONB_STEPS.map(([k, Icon], idx) => {
            const done = idx < doneCount;
            const current = ONB_STEPS[idx][0] === step && !done;
            return (
              <li key={k} className="flex flex-col items-center gap-1.5">
                <span className={cn("flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-500", done ? "border-transparent" : current ? "border-ink/40 bg-surface text-ink" : "border-hairline bg-surface text-ink/30")} style={done ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
                  {done ? <Check className="h-3 w-3" strokeWidth={3} /> : <Icon className="h-3 w-3" strokeWidth={1.9} />}
                </span>
                <span className="h-[3px] w-full rounded-full overflow-hidden bg-hairline"><motion.span className="block h-full w-full origin-left rounded-full" style={{ background: BRAND }} animate={{ scaleX: done ? 1 : 0 }} transition={{ duration: 0.6, ease: EASE }} /></span>
              </li>
            );
          })}
        </motion.ol>

        {/* one focused step at a time */}
        <div className="relative mt-3.5 flex-1 min-h-0">
          <AnimatePresence mode="wait" initial={false}>
            {step === "wifi" && (
              <motion.div key="wifi" variants={onbCard} initial="hidden" animate="show" exit="exit" className="rounded-[16px] border border-hairline bg-surface p-3.5 will-change-transform" data-testid="hero-onb-wifi">
                <motion.div variants={item} className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-canvasAlt"><Wifi className="h-4 w-4" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
                  <div className="min-w-0"><p className="text-[13.5px] font-medium text-ink leading-tight">Wi-Fi</p><p className="text-[11px] text-ink/55 truncate">{t("l5.ph.onb_wifi_hint")}</p></div>
                </motion.div>
                <motion.div variants={item} className="mt-3 relative h-[96px] rounded-[12px] overflow-hidden border border-hairline bg-canvasAlt">
                  <AnimatePresence mode="wait" initial={false}>
                    {!photo ? (
                      <motion.div key="cam" exit={{ opacity: 0 }} className="absolute inset-0 flex items-center justify-center">
                        <motion.span animate={shutter ? { scale: [1, 0.9, 1] } : { scale: 1 }} transition={{ duration: 0.3 }} className="relative overflow-hidden inline-flex items-center gap-2 h-9 rounded-full px-3.5 text-[12px] font-medium" style={{ background: BRAND, color: BRAND_FG }}><Camera className="h-3.5 w-3.5" /> {t("l5.ph.onb_take_photo")}<TapRipple when={shutter} reduced={reduced} /></motion.span>
                      </motion.div>
                    ) : (
                      <motion.div key="img" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="absolute inset-0 bg-[#2B2925]">
                        {/* the router */}
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-[136px] rounded-[6px] bg-[#3A3731] border border-white/10" />
                        <span className="absolute left-1/2 top-1/2 -translate-x-[56px] -translate-y-[6px] h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
                        <span className="absolute left-1/2 top-1/2 -translate-x-[44px] -translate-y-[6px] h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
                        <span className="absolute left-1/2 top-1/2 -translate-x-[10px] translate-y-[2px] h-[7px] w-[54px] rounded-[2px] bg-white/85" />
                        <span className="absolute left-1/2 top-1/2 translate-x-[48px] -translate-y-[30px] h-6 w-1 rounded-full bg-[#3A3731]" />
                        <span className="absolute left-1/2 top-1/2 translate-x-[58px] -translate-y-[30px] h-6 w-1 rounded-full bg-[#3A3731]" />
                        {/* AI scan line */}
                        {!read && !reduced && <motion.span initial={{ y: -8 }} animate={{ y: 100 }} transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }} className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${"var(--demo-brand)"}, transparent)` }} />}
                        {read && <motion.span initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, ease: EASE }} className="absolute left-1/2 top-1/2 -translate-x-[37px] -translate-y-[1px] h-[13px] w-[60px] rounded-[3px] border-2" style={{ borderColor: BRAND }} />}
                        <span className="absolute left-2.5 bottom-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2 py-0.5 text-[9.5px] font-medium text-ink"><AiTag /> {read ? t("l5.ph.onb_read_done") : t("l5.ph.onb_reading")}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>{shutter && !read && <motion.span key="flash" initial={{ opacity: 0.9 }} animate={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0 bg-white pointer-events-none" />}</AnimatePresence>
                </motion.div>
                <div className="mt-3 space-y-2">
                  {[["l5.ph.onb_network", "Prata_Guest"], ["l5.ph.onb_password", "••••••••••"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3 h-10 rounded-[10px] border border-hairline bg-canvas px-3 text-[12.5px]">
                      <span className="text-ink/55">{t(k)}</span>
                      <AnimatePresence mode="wait" initial={false}>
                        {read ? <motion.span key="v" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="text-ink font-medium">{v}</motion.span> : <motion.span key="s" exit={{ opacity: 0 }} className="h-2 w-16 rounded-full bg-fill" />}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
                <motion.div variants={item} className="mt-3">
                  <BrandButton muted={!read} pressed={confirm} reduced={reduced} className="h-10 text-[12.5px]"><Check className="h-3.5 w-3.5" strokeWidth={3} /> {t("l5.ph.onb_looks_right")}</BrandButton>
                </motion.div>
              </motion.div>
            )}

            {step === "rules" && (
              <motion.div key="rules" variants={onbCard} initial="hidden" animate="show" exit="exit" className="rounded-[16px] border border-hairline bg-surface p-3.5 will-change-transform" data-testid="hero-onb-rules">
                <motion.div variants={item} className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-canvasAlt"><ScrollText className="h-4 w-4" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
                  <div className="min-w-0"><p className="text-[13.5px] font-medium text-ink leading-tight">{t("l5.ph.onb_rules")}</p><p className="text-[11px] text-ink/55 truncate">{t("l5.ph.onb_rules_hint")}</p></div>
                </motion.div>
                <motion.div variants={item} className="mt-3 flex flex-wrap gap-2">
                  {RULES.map((r, idx) => {
                    const on = [r1, r2, r3, false][idx];
                    return (
                      <motion.span key={r} animate={on ? { scale: [1, 0.95, 1] } : { scale: 1 }} transition={{ duration: 0.3 }} className={cn("relative overflow-hidden inline-flex items-center gap-1.5 h-9 rounded-full border px-3 text-[12px] transition-colors duration-500", on ? "" : "border-hairline bg-canvas text-ink/75")} style={on ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
                        {on && <Check className="h-3 w-3" strokeWidth={3} />}{r}
                        <TapRipple when={on} reduced={reduced} />
                      </motion.span>
                    );
                  })}
                </motion.div>
                <motion.div variants={item} className="mt-3 h-10 rounded-[10px] border border-hairline bg-canvas px-3 flex items-center text-[12px] text-ink/45">{t("l5.ph.onb_rules_more")}</motion.div>
                <motion.div variants={item} className="mt-3">
                  <BrandButton pressed={rulesTap} reduced={reduced} className="h-10 text-[12.5px]">{t("l5.ph.onb_continue")} <ArrowRight className="h-3.5 w-3.5" /></BrandButton>
                </motion.div>
              </motion.div>
            )}

            {step === "photos" && (
              <motion.div key="photos" variants={onbCard} initial="hidden" animate="show" exit="exit" className="rounded-[16px] border border-hairline bg-surface p-3.5 will-change-transform" data-testid="hero-onb-photos">
                <motion.div variants={item} className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-canvasAlt"><ImageIcon className="h-4 w-4" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
                  <div className="min-w-0"><p className="text-[13.5px] font-medium text-ink leading-tight">{t("l5.ph.onb_photos")}</p><p className="text-[11px] text-ink/55 truncate">{t("l5.ph.onb_photos_hint", { brand: b.name })}</p></div>
                </motion.div>
                <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid="hero-onb-photo-grid">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <motion.span key={i} initial={reduced ? false : { opacity: 0, scale: 0.7, y: 8 }} animate={photosIn ? { opacity: 1, scale: 1, y: 0 } : undefined} transition={{ delay: i * 0.055, duration: 0.45, ease: EASE }} className="relative aspect-square overflow-hidden rounded-[8px] bg-canvasAlt will-change-transform">
                      <img src={ONB_PHOTOS[i % ONB_PHOTOS.length]} alt="" loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                    </motion.span>
                  ))}
                </div>
                <Appear when={photosIn} className="mt-3 flex items-center gap-2 rounded-[10px] bg-successBg px-3 h-10 text-[12px] font-medium text-success"><Tick reduced={reduced} size={16} /> <span className="truncate">{t("l5.ph.onb_photos_done")}</span></Appear>
              </motion.div>
            )}

            {step === "calendar" && (
              <motion.div key="cal" variants={onbCard} initial="hidden" animate="show" exit="exit" className="rounded-[16px] border border-hairline bg-surface p-3.5 will-change-transform" data-testid="hero-onb-calendar">
                <motion.div variants={item} className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-canvasAlt"><CalendarDays className="h-4 w-4" style={{ color: BRAND_TEXT }} strokeWidth={1.9} /></span>
                  <div className="min-w-0"><p className="text-[13.5px] font-medium text-ink leading-tight">{t("l5.ph.onb_cal")}</p><p className="text-[11px] text-ink/55 truncate">{t("l5.ph.onb_cal_hint")}</p></div>
                </motion.div>
                <motion.div variants={item} className="mt-3 flex items-center justify-between"><FieldLabel>{t("l5.ph.onb_cal_month")}</FieldLabel></motion.div>
                <motion.div variants={item} className="mt-2 grid grid-cols-7 gap-1" data-testid="hero-onb-days" data-picked={dayPick ? "20" : ""}>
                  {ONB_DAYS.map(([d, n]) => {
                    const active = dayPick && n === "20";
                    const past = Number(n) < 20;
                    return (
                      <motion.span key={n} animate={active ? { scale: [1, 0.94, 1] } : { scale: 1 }} transition={{ duration: 0.35, ease: EASE }} className={cn("relative overflow-hidden h-[52px] rounded-[10px] border flex flex-col items-center justify-center transition-colors duration-500", active ? "" : past ? "border-transparent bg-transparent text-ink/30" : "border-hairline bg-surface text-ink")} style={active ? { background: BRAND, borderColor: BRAND, color: BRAND_FG } : undefined}>
                        <span className="text-[9px] uppercase tracking-[0.08em] opacity-70">{d}</span>
                        <span className="mt-0.5 text-[13px] font-medium tabular-nums">{n}</span>
                        <TapRipple when={active} reduced={reduced} />
                      </motion.span>
                    );
                  })}
                </motion.div>
                <Appear when={dayPick} className="mt-3 flex items-center gap-2 rounded-[10px] bg-successBg px-3 h-10 text-[12px] font-medium text-success"><Tick reduced={reduced} size={16} /> <span className="truncate">{t("l5.ph.onb_cal_done")}</span></Appear>
              </motion.div>
            )}

            {step === "ready" && (
              <motion.div key="ready" variants={onbCard} initial="hidden" animate="show" exit="exit" className="relative overflow-hidden rounded-[16px] border border-hairline bg-surface p-4 will-change-transform" data-testid="hero-onb-done">
                <span aria-hidden className="pointer-events-none absolute -top-16 left-1/2 h-40 w-64 -translate-x-1/2 rounded-full opacity-70" style={{ background: "radial-gradient(closest-side, var(--demo-brand-glow), transparent 72%)", filter: "blur(18px)" }} />
                <motion.div variants={item} className="relative flex flex-col items-center text-center pt-1">
                  <motion.span initial={reduced ? false : { scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }} className="flex h-12 w-12 items-center justify-center rounded-full" style={{ background: BRAND, color: BRAND_FG }}><Check className="h-5 w-5" strokeWidth={3} /></motion.span>
                  <p className="mt-3 font-heading font-bold text-[17px] tracking-[-0.03em] text-ink leading-tight">{t("l5.ph.onb_ready_title")}</p>
                  <p className="mt-1 text-[11.5px] text-ink/55 leading-snug max-w-[24ch]">{t("l5.ph.onb_ready_sub", { brand: b.name })}</p>
                </motion.div>
                <motion.ul variants={item} className="relative mt-4 space-y-1.5">
                  {[["wifi", "Wi-Fi", t("l5.ph.wifi_read")], ["rules", t("l5.ph.onb_rules"), "3"], ["photos", t("l5.ph.onb_photos"), "12"], ["calendar", t("l5.ph.onb_cal"), "20 May"]].map(([k, label, meta]) => (
                    <li key={k} className="flex items-center gap-2.5 rounded-[10px] bg-canvas px-3 h-9 text-[12px]">
                      <span className="flex-1 text-ink truncate">{label}</span>
                      <span className="text-[10.5px] text-ink/50">{meta}</span>
                      <Tick reduced={reduced} size={15} />
                    </li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* the handoff — a quiet system banner: Listed has created the property in the PMS */}
        <AnimatePresence>
          {handoff && (
            <motion.div key="handoff" initial={{ opacity: 0, y: -24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12 }} transition={{ type: "spring", stiffness: 320, damping: 26 }} className="absolute left-3 right-3 top-1 z-30 flex items-center gap-2.5 rounded-[16px] bg-ink/92 backdrop-blur-md px-3 py-2.5 text-white shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)] will-change-transform" data-testid="hero-onb-handoff">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-white/10"><Server className="h-4 w-4" strokeWidth={1.9} /></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2"><span className="text-[12px] font-medium truncate"><span className="brand-word brand-word-light text-[12px]">Listed</span> · {t("l5.ph.onb_handoff_title")}</span><span className="text-[10px] text-white/50 shrink-0">{t("l5.ph.onb_now")}</span></span>
                <span className="block text-[11px] text-white/65 truncate">{t("l5.ph.onb_handoff_sub")}</span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

/* live */
function ScreenLive({ reduced }) {
  return (
    <>
      <TenantBar owner />
      <div className="flex-1 flex flex-col min-h-0">
        <motion.div variants={item} className="relative h-[150px] overflow-hidden shrink-0">
          <img src="/img/interior-openplan.webp" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <span className="absolute left-4 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[10.5px] font-medium text-ink"><span className="h-1.5 w-1.5 rounded-full bg-success" /> {t("l4.frame.status.live")}</span>
        </motion.div>
        <div className="px-[18px] pt-4 pb-4 flex-1 flex flex-col">
          <motion.h4 variants={item} className="font-heading font-bold text-[20px] tracking-[-0.03em] text-ink leading-tight">{t("l5.ph.live_title")}</motion.h4>
          <motion.p variants={item} className="mt-1.5 text-[12.5px] text-ink/60 leading-[1.5]">{t("l5.ph.live_sub")}</motion.p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {["airbnb", "booking"].map((id, idx) => (
              <motion.span key={id} initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 + idx * 0.35, duration: 0.45, ease: EASE }} className="h-11 rounded-[12px] border border-hairline bg-surface flex items-center justify-center text-ink/85"><BrandLogo id={id} scale={0.6} /></motion.span>
            ))}
          </div>
          {/* the payoff — the first booking request arrives */}
          <motion.div initial={reduced ? false : { opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={reduced ? { duration: 0 } : { delay: 2.3, type: "spring", stiffness: 300, damping: 24 }} className="mt-3 rounded-[14px] border border-hairline bg-surface p-3 flex items-center gap-3 shadow-[0_10px_28px_-18px_rgba(21,19,15,0.35)]" data-testid="hero-phone-first-booking">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-heading font-bold" style={{ background: "var(--accent-soft)", color: BRAND_TEXT }}>JM</span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-ink truncate">{t("l5.ph.live_booking_title")}</p>
              <p className="mt-0.5 text-[11px] text-ink/55 truncate">{t("l5.ph.live_booking_meta")}</p>
            </div>
            <span className="shrink-0 font-heading font-bold text-[14px] text-ink tabular-nums">{t("l5.ph.live_booking_amount")}</span>
          </motion.div>
          <motion.div variants={item} className="mt-auto rounded-[14px] bg-surface border border-hairline p-3.5 flex items-center justify-between text-[12px]">
            <span className="text-ink/55">{t("l7.d.live.kpi2")}</span><span className="font-heading font-bold text-[15px] text-ink">{t("l7.d.live.kpi2_v")}</span>
          </motion.div>
        </div>
      </div>
    </>
  );
}

/* ======================================================================
   Controlled story (see pages/home/StoryScroll.js) — the same scenes, clock
   and surfaces as the hero story, but the *scroll position* picks the scene.
   A scene plays once from 0 and holds on its last frame; nothing auto-advances.
====================================================================== */
const StoryCtx = createContext(null);
export const useStory = () => useContext(StoryCtx);
export { Beats as StoryBeats, ChapterBar as StoryChapterBar, GROUP_OF as STORY_GROUP_OF };

/** `frozen`: render the scene's finished state at once (no clock) — a still frame. `surface` overrides
    which side of the moment is shown (phone | desktop) regardless of the scene's own switch. */
export function StoryProvider({ brand: brandProp, scenes: scenesProp, index = 0, playing = true, frozen = false, surface: surfaceProp, className, style, children, ...rest }) {
  const brand = { ...DEFAULT_BRAND, ...(brandProp || {}) };
  brandVars.brand = brand.name;
  brandVars.domain = brand.domain;
  brandVars.ownerDomain = brand.ownerDomain;
  const scenes = scenesProp || SCENES;
  const motionOk = !useReducedMotion();
  const reduced = !motionOk || frozen;
  const clockRef = useRef(null);
  if (!clockRef.current) clockRef.current = createClock();
  const clock = clockRef.current;
  const i = Math.max(0, Math.min(scenes.length - 1, index));
  const scene = scenes[i];
  // The screens (Desktop timeline, pipeline "arrived") reason in full-story scene order.
  const sceneIndex = IDS.indexOf(scene.id);
  const [cycle, setCycle] = useState(0);
  // A new scene, or a new brand (the visitor's site just loaded) → the clock restarts and the screens mount fresh.
  const brandSig = `${brand.name}|${brand.color}|${brand.logoUrl || ""}`;
  const sigRef = useRef(`${i}|${brandSig}`);
  useLayoutEffect(() => {
    const sig = `${i}|${brandSig}`;
    if (sigRef.current === sig) return;
    sigRef.current = sig;
    clock.reset(0);
    setCycle((c) => c + 1);
  }, [i, brandSig, clock]);
  useEffect(() => {
    if (playing && !reduced) clock.start();
    else clock.stop();
  }, [playing, reduced, clock]);
  useEffect(() => () => clock.stop(), [clock]);
  const sceneKey = `${scene.id}-${cycle}`;
  const switched = useAtWith(clock, scene.switchAt || 0, reduced, sceneKey) && !!scene.second;
  const surface = surfaceProp || (switched ? scene.second : scene.first);
  const offset = scene.second && surface === scene.second ? scene.switchAt : 0;
  const done = useAtWith(clock, scene.ms, reduced, sceneKey);
  const value = useMemo(
    () => ({ brand, scene, index: i, sceneIndex, total: scenes.length, cycle, sceneKey, surface, offset, clock, reduced, motionOk, done, playing, group: GROUP_OF[scene.id] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandSig, brand.domain, brand.heroImageUrl, scene, i, sceneIndex, scenes.length, cycle, sceneKey, surface, offset, clock, reduced, motionOk, done, playing]
  );
  return (
    <DemoBrand.Provider value={brand}>
      <ClockCtx.Provider value={clock}>
        <StoryCtx.Provider value={value}>
          <div className={cn("demo-brand-scope", className)} style={{ ...brandStyle(brand.color), ...(style || {}) }} data-testid="story-provider" data-stage={scene.id} data-surface={surface} data-done={done ? "true" : "false"} data-playing={playing ? "true" : "false"} data-brand={brand.name} {...rest}>
            {/* warm the image cache so later screens never pop */}
            <span aria-hidden className="hidden"><img src={THUMB} alt="" /><img src={LIVE_IMG} alt="" /><img src={SITE_IMG} alt="" />{ONB_PHOTOS.map((p) => <img key={p} src={p} alt="" />)}</span>
            {children}
          </div>
        </StoryCtx.Provider>
      </ClockCtx.Provider>
    </DemoBrand.Provider>
  );
}

/** The current scene's surface, rendered at the fixed design size and scaled to fit `width` × `height`.
    Picks the compact / mid / wide design from the available width, like the hero stage does. */
export function StoryCanvas({ width, height, className }) {
  const s = useStory();
  const ref = useRef(null);
  const compact = width > 0 && width < 560;
  const mid = !compact && width > 0 && width < 900;
  const design = compact ? COMPACT : mid ? MID : STAGE;
  const scale = width > 0 && height > 0 ? Math.min(width / design.w, height / design.h) : 0;
  const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const tilt = useTilt(finePointer && s.motionOk && s.surface === "phone", ref);
  const phoneLeft = (design.w - PHONE.w) / 2;
  if (!scale) return <div ref={ref} className={className} aria-hidden />;
  return (
    <div ref={ref} className={cn("relative mx-auto", className)} style={{ width: Math.round(design.w * scale), height: Math.round(design.h * scale) }} data-testid="story-canvas" data-design={compact ? "compact" : mid ? "mid" : "wide"} data-scale={scale.toFixed(3)}>
      <div className="absolute left-0 top-0" style={{ width: design.w, height: design.h, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <Canvas scene={s.scene} index={s.sceneIndex} surface={s.surface} offset={s.offset} reduced={s.reduced} float={s.motionOk} cycle={s.cycle} started compact={compact} mid={mid} phoneLeft={phoneLeft} tilt={tilt} loop={false} />
      </div>
    </div>
  );
}
